import { useState, useMemo, useRef, useEffect, createContext, useContext } from "react";

const isMobile = window.innerWidth <= 768

// ─── DATA ────────────────────────────────────────────────────────────────────
function createModel(p) {
  // category can be "Futebol, Vôlei" — split into array, keep original string too
  const categoryStr = p.category || "";
  const categories = categoryStr.split(",").map(s => s.trim()).filter(Boolean);
  const category = categories[0] || ""; // primary category (for color/compat)
  return {
    id: p.id, name: p.name,
    category,             // primary (first) category — for color keys & compat
    categories,           // all categories from the comma-separated field
    categoryStr,          // original raw string
    price: p.price,
    sizes: p.sizes || "", fabric: p.fabric || "", collar: p.collar || "",
    sleeve: p.sleeve || "", shield: p.shield || "", extras: p.extras || "",
    thumb: p.thumb || null, fabricImg: p.fabricImg || null, shieldImg: p.shieldImg || null,
    description: p.description || "",
    path: p.path || "",
    fabrics: p.fabrics || [],
    shields: p.shields || [],
    designs: p.designs || [],
  };
}

// ─── SAMPLE FABRIC / SHIELD / DESIGN DATA ──────────────────────────────────
// In production populate these per-model. Here we define shared option pools.
const FABRIC_OPTIONS = [
  { id: "dry_fit", name: "Dry Fit", img: null },
  { id: "dry_colmeia", name: "Dry Texturizado Colmeia", img: null },
  { id: "aerodry", name: "Aerodry", img: null },
  { id: "slide_dry", name: "Slide Dry", img: null },
  { id: "thermo_dry", name: "Thermo Dry", img: null },
  { id: "meia_malha", name: "Meia-Malha Algodão", img: null },
  { id: "moletom", name: "Moletom Felpado", img: null },
  { id: "jacquard", name: "Jacquard 3D", img: null },
  { id: "square", name: "Square", img: null },
  { id: "malha_adis", name: "Malha Sport Adis", img: null },
];
const SHIELD_OPTIONS = [
  { id: "sublimado", name: "Sublimado", img: null },
  { id: "bordado", name: "Bordado", img: null },
  { id: "estampa_dig", name: "Estampa Digital", img: null },
  { id: "bordado_logo", name: "Logo Bordado", img: null },
];
// Designs are per-model — use model.designs array. Placeholder pool:
const DESIGN_POOL = [
  { id: "d1", name: "Clássico", img: null, tags: ["todos"] },
  { id: "d2", name: "Listrado", img: null, tags: ["todos"] },
  { id: "d3", name: "Geométrico", img: null, tags: ["todos"] },
  { id: "d4", name: "Degradê", img: null, tags: ["todos"] },
  { id: "d5", name: "Tribal", img: null, tags: ["todos"] },
  { id: "d6", name: "Minimalista", img: null, tags: ["todos"] },
  { id: "d7", name: "Camuflado", img: null, tags: ["todos"] },
  { id: "d8", name: "Chamas", img: null, tags: ["todos"] },
  { id: "d9", name: "Xadrez", img: null, tags: ["todos"] },
  { id: "d10", name: "Ondas", img: null, tags: ["todos"] },
  { id: "d11", name: "Sport", img: null, tags: ["todos"] },
  { id: "d12", name: "Premium", img: null, tags: ["todos"] },
];

const MODELS = [
  createModel({ id: 1, name: "Swift", category: "Camisas", price: 89.90, sizes: "P ao X4 / Infantil 2-14", fabric: "Dry Fit", collar: "Gola V Ribana Canelada", sleeve: "Normal", shield: "Sublimado", extras: "", description: "Camisa esportiva de alta performance com tecido dry fit respirável e gola V em ribana canelada. Ideal para treinos e jogos." }),
  createModel({ id: 2, name: "Fusion", category: "Camisas", price: 94.90, sizes: "P ao X4", fabric: "Dry Texturizado Colmeia", collar: "Gola Redonda c/ Detalhe", sleeve: "Normal", shield: "Sublimado", extras: "Faixa ombro/mangas, barra arredondada", description: "Tecido texturizado colmeia com maior ventilação. Faixa decorativa no ombro e mangas e barra arredondada." }),
  createModel({ id: 3, name: "Eclipse", category: "Camisas", price: 89.90, sizes: "P ao X4", fabric: "Dry Fit", collar: "Gola Redonda", sleeve: "Raglan", shield: "Sublimado", extras: "", description: "Manga raglan para maior liberdade de movimentos. Tecido dry fit leve e durável." }),
  createModel({ id: 4, name: "Prime Fit", category: "Camisas", price: 94.90, sizes: "P ao X4 / Infantil 2-14", fabric: "Dry Fit", collar: "Gola Y", sleeve: "Normal", shield: "Sublimado", extras: "Gola e punho sublimado", description: "Gola Y com detalhe sublimado e punho personalizado. Alta elasticidade e conforto." }),
  createModel({ id: 5, name: "Alfa", category: "Camisas", price: 99.90, sizes: "P ao X4", fabric: "Dry Texturizado Colmeia", collar: "Gola Alfa Ribana Canelada", sleeve: "Normal", shield: "Sublimado", extras: "", description: "Gola Alfa exclusiva em ribana canelada. Tecido colmeia premium para máxima respirabilidade." }),
  createModel({ id: 6, name: "Alfa 2K", category: "Camisas", price: 104.90, sizes: "P ao X4", fabric: "Dry Fit", collar: "Gola Y", sleeve: "Raglan", shield: "Sublimado", extras: "Gola e punho sublimado", description: "Combinação de manga raglan com gola Y sublimada. Design moderno e esportivo." }),
  createModel({ id: 7, name: "Hexá", category: "Camisas", price: 109.90, sizes: "P ao X4", fabric: "Dry Texturizado Colmeia", collar: "Ribana Canelada", sleeve: "Normal", shield: "Bordado (incluso)", extras: "Recorte lateral, abertura c/ botão", description: "Recorte lateral exclusivo e abertura com botão. Escudo bordado já incluso no valor." }),
  createModel({ id: 8, name: "Evolution", category: "Camisas", price: 109.90, sizes: "P ao X4 / Infantil 2-14", fabric: "Dry Texturizado Colmeia", collar: "Ribana Canelada", sleeve: "Normal", shield: "Bordado (incluso)", extras: "Recorte no ombro", description: "Recorte exclusivo no ombro para identidade visual única. Escudo bordado incluso." }),
  createModel({ id: 9, name: "Alfa Premium", category: "Camisas", price: 124.90, sizes: "P ao X4", fabric: "Dry Colmeia + Aerodry", collar: "Ribana c/ Viés", sleeve: "Normal", shield: "Bordado (incluso)", extras: "Acabamento viés na gola", description: "Tecido duplo: colmeia na frente e aerodry nas laterais. Acabamento viés premium na gola." }),
  createModel({ id: 10, name: "Alfa Authentic", category: "Camisas", price: 134.90, sizes: "P ao X4", fabric: "Frontal Texturizado + Thermo Dry", collar: "Ribana c/ Viés", sleeve: "Normal", shield: "Bordado (incluso)", extras: "Logo King emborrachado", description: "Frontal texturizado geométrico com costas em thermo dry de alta performance. Logo King emborrachado." }),
  createModel({ id: 11, name: "Squadra", category: "Camisas", price: 119.90, sizes: "P ao X4 / Infantil 2-14", fabric: "Square + Aerodry", collar: "Decote Sublimável", sleeve: "Normal", shield: "Bordado (incluso)", extras: "Logo King emborrachado", description: "Tecido square exclusivo com lateral aerodry. Decote com recorte interno sublimável." }),
  createModel({ id: 12, name: "Zenith", category: "Camisas", price: 139.90, sizes: "P ao X4", fabric: "Slide Dry + Jacquard 3D", collar: "Retilínea", sleeve: "Normal", shield: "Bordado (incluso)", extras: "Selo Produto Oficial", description: "Tecido slide dry premium com recortes em jacquard 3D. Selo produto oficial King Sports." }),
  createModel({ id: 13, name: "Infinity", category: "Camisas", price: 139.90, sizes: "P ao X4", fabric: "Slide Dry + Thermo Dry", collar: "Retilínea", sleeve: "Normal", shield: "Bordado (incluso)", extras: "Barra arredondada", description: "Slide dry na frente com thermo dry nas laterais e ombros. Barra arredondada com acabamento premium." }),
  createModel({ id: 14, name: "Algodão", category: "Camisas", price: 69.90, sizes: "P ao X3", fabric: "Meia-Malha Algodão Penteado", collar: "Gola Redonda", sleeve: "Normal", shield: "Estampa Digital", extras: "", description: "Conforto do algodão penteado com estampa digital de alta resolução. Ideal para uso casual." }),
  createModel({ id: 15, name: "Unif. Swift", category: "Uniformes", price: 149.90, sizes: "P ao X4 / Infantil 2-14", fabric: "Dry Fit", collar: "Gola V Ribana Canelada", sleeve: "Normal", shield: "Sublimado", extras: "Kit: Camisa + Shorts + Meião", description: "Kit completo Swift: camisa, shorts e meião combinando. Tudo em dry fit de qualidade." }),
  createModel({ id: 16, name: "Unif. Prime", category: "Uniformes", price: 164.90, sizes: "P ao X4 / Infantil 2-14", fabric: "Dry Fit", collar: "Gola Y", sleeve: "Normal", shield: "Sublimado", extras: "Kit: Camisa + Shorts + Meião", description: "Kit Prime Fit completo com camisa gola Y, shorts e meião." }),
  createModel({ id: 17, name: "Unif. Eclipse", category: "Uniformes", price: 159.90, sizes: "P ao X4", fabric: "Dry Fit", collar: "Gola Redonda", sleeve: "Raglan", shield: "Sublimado", extras: "Kit: Camisa + Shorts + Meião", description: "Kit Eclipse com manga raglan, shorts e meião. Design esportivo completo." }),
  createModel({ id: 18, name: "Unif. Alfa", category: "Uniformes", price: 199.90, sizes: "P ao X4", fabric: "Dry Colmeia + Aerodry", collar: "Ribana c/ Viés", sleeve: "Normal", shield: "Bordado (incluso)", extras: "Kit: Camisa + Shorts + Meião", description: "Kit premium Alfa: camisa colmeia + aerodry, shorts e meião. Escudo bordado incluso." }),
  createModel({ id: 19, name: "Unif. Squadra", category: "Uniformes", price: 189.90, sizes: "P ao X4 / Infantil 2-14", fabric: "Square + Aerodry", collar: "Decote Sublimável", sleeve: "Normal", shield: "Bordado (incluso)", extras: "Kit: Camisa + Shorts + Meião", description: "Kit Squadra completo. Tecido square exclusivo com lateral aerodry." }),
  createModel({ id: 20, name: "Baby Look", category: "Feminino", price: 79.90, sizes: "P ao X3", fabric: "", collar: "", sleeve: "", shield: "Bordado +valor", extras: "Modelo feminino", description: "Baby look feminino de alta qualidade com corte moderno e confortável." }),
  createModel({ id: 21, name: "BL Prime", category: "Feminino", price: 89.90, sizes: "P ao X3", fabric: "", collar: "", sleeve: "", shield: "Bordado +valor", extras: "Modelo feminino premium", description: "Baby look feminino premium com acabamento diferenciado." }),
  createModel({ id: 22, name: "Polo Feminina", category: "Feminino", price: 99.90, sizes: "P ao X3", fabric: "", collar: "Polo", sleeve: "Manga Curta", shield: "Bordado +valor", extras: "", description: "Polo feminina elegante para eventos corporativos e esportivos." }),
  createModel({ id: 23, name: "Vestido Polo", category: "Feminino", price: 109.90, sizes: "P ao X3", fabric: "", collar: "Polo", sleeve: "Manga Curta", shield: "Bordado +valor", extras: "Modelo vestido", description: "Vestido polo feminino. Elegante e esportivo ao mesmo tempo." }),
  createModel({ id: 24, name: "Michigan", category: "Inverno", price: 159.90, sizes: "P ao X4", fabric: "Moletom Felpado", collar: "Com touca", sleeve: "Manga Longa", shield: "Logo bordado", extras: "Bolsos canguru", description: "Blusa Michigan com moletom felpado, touca integrada e bolsos canguru personalizados." }),
  createModel({ id: 25, name: "College", category: "Inverno", price: 169.90, sizes: "P ao X4", fabric: "Moletom Felpado/Sublimado", collar: "Normal c/ botão pressão", sleeve: "Manga Longa", shield: "Logo bordado", extras: "Bolsos embutidos", description: "Blusa college estilo universitário com moletom premium. Disponível felpado ou sublimado." }),
  createModel({ id: 26, name: "Agasalho Flack", category: "Inverno", price: 249.90, sizes: "P ao X4 / Infantil 4-14", fabric: "Malha Sport Adis", collar: "Sem touca", sleeve: "Manga Longa", shield: "Escudo bordado", extras: "Modelo slim", description: "Agasalho completo slim em malha sport adis. Bolsos frontais e zíper na barra da calça." }),
  createModel({ id: 27, name: "Bermuda New", category: "Bermudas", price: 59.90, sizes: "P ao X3", fabric: "", collar: "", sleeve: "", shield: "Bordado +valor", extras: "", description: "Bermuda esportiva modelo New, confortável e durável para todas as atividades." }),
  createModel({ id: 28, name: "Bermuda Flex", category: "Bermudas", price: 64.90, sizes: "P ao X3", fabric: "", collar: "", sleeve: "", shield: "Bordado +valor", extras: "", description: "Bermuda Flex com material elástico de alta performance." }),
  createModel({ id: 29, name: "Bermuda Lakers", category: "Bermudas", price: 69.90, sizes: "P ao X3", fabric: "", collar: "", sleeve: "", shield: "Bordado +valor", extras: "", description: "Bermuda Lakers estilo basquete, caimento amplo e confortável." }),
  createModel({ id: 30, name: "Regata", category: "Regatas", price: 54.90, sizes: "P ao X3", fabric: "", collar: "", sleeve: "Sem manga", shield: "Bordado +valor", extras: "", description: "Regata esportiva clássica para treinos intensos e jogos." }),
  createModel({ id: 31, name: "Regata Rio", category: "Regatas", price: 54.90, sizes: "P ao X3", fabric: "", collar: "", sleeve: "Sem manga", shield: "Bordado +valor", extras: "", description: "Regata Rio com corte diferenciado e tecido ultra-leve." }),
  createModel({ id: 32, name: "Regata Kobe", category: "Regatas", price: 64.90, sizes: "P ao X3", fabric: "", collar: "Gola V", sleeve: "Sem manga", shield: "Bordado +valor", extras: "Estilo basquete", description: "Regata estilo NBA com gola V e corte basquete. Perfeita para quadra." }),
  createModel({ id: 33, name: "Boné Trucker", category: "Bonés", price: 39.90, sizes: "Único ajustável", fabric: "Redinha traseira", collar: "", sleeve: "", shield: "2 bordados", extras: "Mín. 12 unidades", description: "Boné trucker com telinha traseira e 2 bordados inclusos (frente + lateral)." }),
  createModel({ id: 34, name: "Boné Snap", category: "Bonés", price: 44.90, sizes: "Único ajustável", fabric: "Laser perfurado", collar: "", sleeve: "", shield: "2 bordados", extras: "Mín. 12 unidades", description: "Boné snap laser com furos de ventilação e 2 bordados inclusos." }),
];

const CATEGORIES = ["Todos", "Camisas", "Uniformes", "Feminino", "Inverno", "Bermudas", "Regatas", "Bonés"];
const CAT_COLOR = { Camisas: "#e94560", Uniformes: "#3b82f6", Feminino: "#ec4899", Inverno: "#60a5fa", Bermudas: "#f59e0b", Regatas: "#34d399", Bonés: "#a78bfa" };
const PALETTE = ["#e94560", "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#3b82f6", "#8b5cf6", "#ec4899", "#ffffff", "#94a3b8", "#1e293b", "#000000"];
const PROFILE_TYPES = [{ id: "time", label: "Time/Clube", desc: "Uniforme para equipe esportiva" }, { id: "empresa", label: "Empresa", desc: "Uniforme corporativo" }, { id: "evento", label: "Evento", desc: "Uniforme para evento" }, { id: "revenda", label: "Revenda", desc: "Compra para revender" }];
const OBJECTIVES = [{ id: "campeonato", label: "🏆 Campeonato" },{ id: "interclasse", label: "🎽 Interclasse" }, { id: "formandos", label: "🎓 Formandos" }, { id: "treino", label: "⚽ Treino" }, { id: "formatura", label: "🎓 Formatura" }, { id: "carnaval", label: "🎉 Carnaval" }, { id: "evento", label: "🎪 Evento" }, { id: "confra", label: "🍻 Confraternização" }, { id: "empresa", label: "💼 Empresa" }, { id: "torcida", label: "📣 Torcida Organizada" }, { id: "corrida", label: "🏃 Corrida / Trilha" }, { id: "viagem", label: "✈️ Viagem / Tour" }, { id: "casamento", label: "💍 Casamento" }, { id: "familia", label: "👨‍👩‍👧 Família" }, { id: "outro", label: "➕ Outro" }];
const ROLES = ["Presidente / Diretor", "Gerente / Coordenador", "Técnico / Comissão técnica", "Atleta / Jogador", "Tesoureiro / Financeiro", "Secretário / Administrativo", "Responsável de Marketing", "Voluntário / Colaborador", "Outros"];
const FONT_GROUPS = [
  { label: "Esportivo / Bold", fonts: ["Bebas Neue", "Anton", "Teko", "Barlow Condensed", "Oswald", "Fjalla One", "Russo One", "Big Shoulders Display", "Chakra Petch", "Rajdhani", "Orbitron"] },
  { label: "Moderno / Clean", fonts: ["Montserrat", "Poppins", "Nunito", "DM Sans", "Outfit", "Urbanist", "Lexend", "Figtree", "Manrope", "Jost", "Raleway", "Mulish", "Karla"] },
  { label: "Display / Impactante", fonts: ["Boogaloo", "Fredoka One", "Lilita One", "Righteous", "Calistoga", "Titan One", "Black Ops One", "Michroma", "Audiowide", "Iceland", "Bangers"] },
  { label: "Manuscrito / Estilizado", fonts: ["Pacifico", "Lobster", "Dancing Script", "Permanent Marker", "Satisfy", "Sacramento", "Yellowtail", "Caveat", "Kaushan Script", "Courgette", "Great Vibes"] },
  { label: "Serifado / Clássico", fonts: ["Playfair Display", "Merriweather", "Lora", "PT Serif", "Libre Baskerville", "Cormorant Garamond", "EB Garamond", "Crimson Text", "Bitter", "Zilla Slab", "Arvo"] },
];
const ALL_FONTS = [...new Set(FONT_GROUPS.flatMap(g => g.fonts))];
const GFONTS_URL = "https://fonts.googleapis.com/css2?" + ALL_FONTS.map(f => `family=${f.replace(/ /g, "+")}:wght@400;700;900`).join("&") + "&display=swap";
const STORE_INFO = { name: "King Sports", tagline: "Uniformes esportivos personalizados", phone: "(11) 99999-0000", whatsapp: "5511999990000", email: "contato@kingsports.com.br", site: "www.kingsports.com.br", instagram: "@kingsports", facebook: "King Sports", address: "Rua dos Uniformes, 123 — São Paulo, SP", hours: "Seg–Sex: 8h–18h  ·  Sáb: 8h–13h", paymentMethods: ["Pix", "Cartão de crédito", "Boleto", "Transferência"], badge: "🏆 +5.000 times atendidos", logoUrl: "" };
let _nid = 500; const nid = () => ++_nid;
const PLBL = { time: "Time/Clube", empresa: "Empresa", evento: "Evento", revenda: "Revenda" };

// ─── THEME ───────────────────────────────────────────────────────────────────
const THEMES = {
  dark: { mode: "dark", bg: "#080810", surface: "#0e0e18", surface2: "#0d0d14", surface3: "#141420", border: "#1a1a2a", border2: "#1e1e2e", border3: "#2a2a3a", text: "#ffffff", textSub: "#cccccc", textMuted: "#888888", textFaint: "#555555", textGhost: "#333333", listBg: "#080810", shadow: "rgba(0,0,0,.5)", cardShadow: "0 20px 50px rgba(0,0,0,.5)" },
  light: { mode: "light", bg: "#f0f2f8", surface: "#ffffff", surface2: "#f7f8fc", surface3: "#eef0f7", border: "#e0e4f0", border2: "#d0d5e8", border3: "#c0c6dc", text: "#0d0d1a", textSub: "#2a2a40", textMuted: "#5a5a78", textFaint: "#8888a8", textGhost: "#b0b0cc", listBg: "#f0f2f8", shadow: "rgba(0,0,0,.1)", cardShadow: "0 8px 32px rgba(60,60,120,.10)" },
};
const ThemeCtx = createContext(THEMES.dark);
const useT = () => useContext(ThemeCtx);
const sInp = T => ({ width: "100%", background: T.surface2, border: `1.5px solid ${T.border2}`, borderRadius: 10, color: T.textSub, padding: "9px 13px", fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color .2s" });
const sLbl = T => ({ fontSize: 10, color: T.textFaint, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 5 });
const sBtnP = () => ({ background: "#e94560", border: "none", borderRadius: 10, color: "#fff", padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" });
const sBtnG = T => ({ background: "transparent", border: `1.5px solid ${T.border3}`, borderRadius: 10, color: T.textMuted, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 7 });

// ─── STORE INFO CONTEXT ───────────────────────────────────────────────────────
const StoreCtx = createContext({});
const useStore = () => useContext(StoreCtx);

const ModelsCtx = createContext([]);
const useModels = () => useContext(ModelsCtx);

// ─── ICONS ───────────────────────────────────────────────────────────────────
const Ic = {
  check: () => <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  cart: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" /></svg>,
  trash: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /><path d="M10 11v6M14 11v6M9 6V4h6v2" /></svg>,
  info: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="8.01" /><path d="M12 11v5" /></svg>,
  cR: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>,
  cL: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>,
  cD: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>,
  srch: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>,
  img: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>,
  x: () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  font: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 7 4 4 20 4 20 7" /><line x1="9" y1="20" x2="15" y2="20" /><line x1="12" y1="4" x2="12" y2="20" /></svg>,
  cal: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
  user: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
  sun: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>,
  moon: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" /></svg>,
  fabric: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg>,
  shield: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  copy: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>,
  trash2: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /><path d="M10 11v6M14 11v6M9 6V4h6v2" /></svg>,
};

// ─── THEME TOGGLE ────────────────────────────────────────────────────────────
function ThemeToggle({ isDark, onToggle }) {
  const T = useT();
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: 10 }}>
      <button id="back-button-iframe-form" style={{
        background: T.surface2, border: `1.5px solid ${T.border2}`, borderRadius: 50,
        padding: "5px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
      }}>
        Fechar
      </button>
      <button id="click-form-theme" onClick={() => {
        onToggle();
        window["changeTheme"]();
      }} title={isDark ? "Tema claro" : "Tema escuro"} style={{
        background: T.surface2, border: `1.5px solid ${T.border2}`, borderRadius: 50,
        padding: "5px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 7,
        color: T.textMuted, fontSize: 11, fontWeight: 600, transition: "all .25s",
      }}>
        <div style={{ width: 30, height: 17, borderRadius: 9, background: isDark ? "#e9456033" : T.border2, position: "relative", transition: "background .25s", flexShrink: 0 }}>
          <div style={{ width: 11, height: 11, borderRadius: "50%", background: isDark ? "#e94560" : T.textGhost, position: "absolute", top: 3, left: isDark ? 16 : 3, transition: "left .25s, background .25s" }} />
        </div>
        <span style={{ color: T.textSub }}>{isDark ? <Ic.moon /> : <Ic.sun />}</span>
        <span className="ks-toggle-text" style={{ color: T.textSub, fontSize: 11 }}>{isDark ? "Escuro" : "Claro"}</span>
      </button>
    </div>

  );
}

// ─── SVG THUMBS ──────────────────────────────────────────────────────────────
function ThumbSVG({ category, color: c, size = 88 }) {
  const svgs = {
    Camisas: (<svg viewBox="0 0 120 100" fill="none"><rect width="120" height="100" fill="#0d0d18" /><path d="M30 20L20 35L35 40L35 80L85 80L85 40L100 35L90 20L75 28Q60 22 45 28Z" fill={c + "33"} stroke={c} strokeWidth="1.5" strokeLinejoin="round" /><path d="M48 28L60 42L72 28" stroke={c} strokeWidth="1.5" fill="none" strokeLinecap="round" /><path d="M30 20L20 35L35 40L38 28Z" fill={c + "44"} stroke={c} strokeWidth="1.2" /><path d="M90 20L100 35L85 40L82 28Z" fill={c + "44"} stroke={c} strokeWidth="1.2" /><text x="60" y="66" textAnchor="middle" fontSize="22" fontWeight="900" fontFamily="Arial Black,sans-serif" fill={c + "66"}>10</text></svg>),
    Uniformes: (<svg viewBox="0 0 120 100" fill="none"><rect width="120" height="100" fill="#0d0d18" /><path d="M32 14L22 28L36 33L36 62L84 62L84 33L98 28L88 14L74 21Q60 16 46 21Z" fill={c + "33"} stroke={c} strokeWidth="1.5" strokeLinejoin="round" /><path d="M46 21L60 33L74 21" stroke={c} strokeWidth="1.5" fill="none" strokeLinecap="round" /><path d="M32 14L22 28L36 33L39 20Z" fill={c + "44"} stroke={c} strokeWidth="1.2" /><path d="M88 14L98 28L84 33L81 20Z" fill={c + "44"} stroke={c} strokeWidth="1.2" /><text x="60" y="53" textAnchor="middle" fontSize="14" fontWeight="900" fontFamily="Arial Black,sans-serif" fill={c + "77"}>10</text><path d="M38 66L38 88L57 88L60 78L63 88L82 88L82 66Z" fill={c + "22"} stroke={c} strokeWidth="1.5" strokeLinejoin="round" /></svg>),
    Feminino: (<svg viewBox="0 0 120 100" fill="none"><rect width="120" height="100" fill="#0d0d18" /><path d="M38 18L28 30L40 35L40 78L80 78L80 35L92 30L82 18L70 24Q60 19 50 24Z" fill={c + "33"} stroke={c} strokeWidth="1.5" strokeLinejoin="round" /><path d="M50 24Q60 32 70 24" stroke={c} strokeWidth="1.5" fill="none" strokeLinecap="round" /><path d="M38 18L28 30L40 35L41 22Z" fill={c + "44"} stroke={c} strokeWidth="1.2" /><path d="M82 18L92 30L80 35L79 22Z" fill={c + "44"} stroke={c} strokeWidth="1.2" /><path d="M40 50Q36 64 40 78" stroke={c + "66"} strokeWidth="1.5" fill="none" /><path d="M80 50Q84 64 80 78" stroke={c + "66"} strokeWidth="1.5" fill="none" /></svg>),
    Inverno: (<svg viewBox="0 0 120 100" fill="none"><rect width="120" height="100" fill="#0d0d18" /><path d="M28 22L16 40L34 46L34 84L86 84L86 46L104 40L92 22L76 30Q60 24 44 30Z" fill={c + "22"} stroke={c} strokeWidth="1.5" strokeLinejoin="round" /><path d="M44 30Q60 16 76 30Q76 40 60 44Q44 40 44 30Z" fill={c + "44"} stroke={c} strokeWidth="1.5" /><rect x="44" y="62" width="32" height="16" rx="3" fill={c + "22"} stroke={c} strokeWidth="1.2" /><line x1="60" y1="46" x2="60" y2="84" stroke={c + "55"} strokeWidth="1" strokeDasharray="3,2" /></svg>),
    Bermudas: (<svg viewBox="0 0 120 100" fill="none"><rect width="120" height="100" fill="#0d0d18" /><rect x="25" y="18" width="70" height="10" rx="3" fill={c + "55"} stroke={c} strokeWidth="1.2" /><path d="M25 28L25 82L56 82L60 60L64 82L95 82L95 28Z" fill={c + "22"} stroke={c} strokeWidth="1.5" strokeLinejoin="round" /><line x1="60" y1="28" x2="60" y2="60" stroke={c} strokeWidth="1.2" /></svg>),
    Regatas: (<svg viewBox="0 0 120 100" fill="none"><rect width="120" height="100" fill="#0d0d18" /><path d="M44 14L36 14L30 24L30 82L90 82L90 24L84 14L76 14L72 22Q60 18 48 22Z" fill={c + "22"} stroke={c} strokeWidth="1.5" strokeLinejoin="round" /><path d="M44 14L48 22" stroke={c} strokeWidth="3" strokeLinecap="round" /><path d="M76 14L72 22" stroke={c} strokeWidth="3" strokeLinecap="round" /><text x="60" y="62" textAnchor="middle" fontSize="20" fontWeight="900" fontFamily="Arial Black,sans-serif" fill={c + "66"}>7</text></svg>),
    Bonés: (<svg viewBox="0 0 120 100" fill="none"><rect width="120" height="100" fill="#0d0d18" /><path d="M20 62Q20 24 60 20Q100 24 100 62Z" fill={c + "22"} stroke={c} strokeWidth="1.5" /><path d="M16 64Q60 74 104 64L100 62Q60 70 20 62Z" fill={c + "55"} stroke={c} strokeWidth="1.5" /><circle cx="60" cy="22" r="3" fill={c} /><rect x="52" y="60" width="16" height="5" rx="2" fill={c + "66"} stroke={c} strokeWidth="1" /><rect x="50" y="40" width="20" height="14" rx="3" fill={c + "33"} stroke={c} strokeWidth="1" /></svg>),
  };
  return <div style={{ width: size, height: size, borderRadius: 10, overflow: "hidden", flexShrink: 0, background: "#0d0d18", border: `1px solid ${c}22` }}>{svgs[category] || svgs.Camisas}</div>;
}
function ModelThumb({ model, size = 88 }) {
  const cc = CAT_COLOR[model.category] || "#888";
  if (model.thumb) return <img src={model.thumb} alt={model.name} style={{ width: size, height: size, objectFit: "contain", borderRadius: 10, flexShrink: 0 }} />;
  return <ThumbSVG category={model.category} color={cc} size={size} />;
}

// ─── MEDIA PLACEHOLDER SVG ───────────────────────────────────────────────────
function MediaPlaceholder({ label, cc, T }) {
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: T.surface3, gap: 6 }}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={cc + "66"} strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
      <span style={{ fontSize: 10, color: T.textFaint, textAlign: "center", padding: "0 8px" }}>{label}</span>
    </div>
  );
}

// ─── SELECTABLE OPTION CARD ───────────────────────────────────────────────────
function OptionCard({ item, selected, onSelect, cc, T, type }) {
  const isSel = selected === item.id;
  const [hovered, setHovered] = useState(false);
  const active = isSel || hovered;
  return (
    <button
      onClick={() => onSelect(isSel ? null : item.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: isSel ? `${cc}22` : hovered ? `${cc}0e` : T.surface2,
        border: isSel ? `2px solid ${cc}` : hovered ? `2px solid ${cc}88` : `1.5px solid ${T.border}`,
        borderRadius: 12, cursor: "pointer", padding: 0, overflow: "hidden",
        display: "flex", flexDirection: "column",
        transition: "all .15s",
        boxShadow: isSel
          ? `0 0 0 1px ${cc}55, 0 4px 20px ${cc}44`
          : hovered ? `0 4px 16px ${cc}22` : "none",
        transform: hovered && !isSel ? "translateY(-2px)" : "none",
        position: "relative",
      }}
    >
      {isSel && (
        <div style={{ position: "absolute", top: 6, right: 6, width: 18, height: 18, borderRadius: "50%", background: cc, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, boxShadow: `0 2px 8px ${cc}88` }}>
          <Ic.check />
        </div>
      )}
      {hovered && !isSel && (
        <div style={{ position: "absolute", inset: 0, background: `${cc}08`, zIndex: 1, pointerEvents: "none", borderRadius: 10 }} />
      )}
      <div style={{ width: "100%", aspectRatio: "1", overflow: "hidden" }}>
        {item.img
          ? <img src={item.img} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "contain", transition: "transform .2s", transform: hovered ? "scale(1.05)" : "scale(1)" }} />
          : <MediaPlaceholder label={type === "design" ? "Design" : type === "fabric" ? "Tecido" : "Escudo"} cc={cc} T={T} />
        }
      </div>
      <div style={{ padding: "6px 8px", fontSize: 11, fontWeight: active ? 700 : 500, color: active ? cc : T.textSub, textAlign: "center", borderTop: `1px solid ${active ? `${cc}44` : T.border}`, lineHeight: 1.3, transition: "color .15s" }}>
        {item.name}
      </div>
    </button>
  );
}

// ─── MODEL INFO MODAL (read-only) ────────────────────────────────────────────
function ModelInfoModal({ model, onClose }) {
  const T = useT();
  const cc = CAT_COLOR[model.category] || "#888";

  const fabricList = model.fabrics || [];
  const shieldList = model.shields || [];

  const specs = [
    model.sizes && { icon: "📐", label: "Tamanhos", val: model.sizes },
    model.collar && { icon: "👕", label: "Gola", val: model.collar },
    model.sleeve && { icon: "💪", label: "Manga", val: model.sleeve },
    model.extras && { icon: "✦", label: "Detalhes", val: model.extras },
  ].filter(Boolean);

  // Read-only display card for fabric/shield options
  const ViewCard = ({ item, type }) => (
    <div style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div style={{ width: "100%", aspectRatio: "1", overflow: "hidden" }}>
        {item.img
          ? <img src={item.img} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          : <MediaPlaceholder label={type === "fabric" ? "Tecido" : "Escudo"} cc={cc} T={T} />
        }
      </div>
      <div style={{ padding: "6px 8px", fontSize: 11, color: T.textSub, textAlign: "center", borderTop: `1px solid ${T.border}`, fontWeight: 500 }}>
        {item.name}
      </div>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000c", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }} onClick={onClose}>
      <div style={{ background: T.surface, border: `1.5px solid ${T.border3}`, borderRadius: 20, width: "100%", maxWidth: 600, maxHeight: "92vh", display: "flex", flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 9, background: T.surface3, flexShrink: 0 }} src={`./form/${model.id}.webp`} alt={model.name} />
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h3 style={{ color: T.text, fontWeight: 800, fontSize: 16, margin: 0 }}>{model.name}</h3>
                <span style={{ fontSize: 9, background: `${cc}22`, color: cc, borderRadius: 5, padding: "2px 7px", fontWeight: 700, textTransform: "uppercase" }}>{model.category}</span>
              </div>
              <div style={{ fontSize: 14, color: cc, fontWeight: 800, marginTop: 2 }}>R$ {model.price.toFixed(2).replace(".", ",")}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: T.surface2, border: "none", borderRadius: 9, color: T.textMuted, padding: "6px 8px", cursor: "pointer", display: "flex", flexShrink: 0 }}><Ic.x /></button>
        </div>

        {/* Body — read only */}
        <div style={{ overflowY: "auto", flex: 1, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 18 }}>

          {/* Description */}
          {model.description && (
            <p style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6, margin: 0, background: T.surface2, padding: "10px 13px", borderRadius: 10, border: `1px solid ${T.border}` }}>{model.description}</p>
          )}

          {/* Specs */}
          {specs.length > 0 && (
            <div>
              <div style={{ fontSize: 10, color: T.textFaint, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>Especificações</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px,1fr))", gap: 7 }}>
                {specs.map((s, i) => (
                  <div key={i} style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 9, padding: "9px 11px", display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span style={{ fontSize: 15, flexShrink: 0 }}>{s.icon}</span>
                    <div>
                      <div style={{ fontSize: 9, color: T.textFaint, textTransform: "uppercase", letterSpacing: .8, marginBottom: 1 }}>{s.label}</div>
                      <div style={{ fontSize: 12, color: T.textSub, fontWeight: 600 }}>{s.val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fabrics — view only */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.textFaint, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 10 }}>
              <Ic.fabric /> Tecidos disponíveis
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px,1fr))", gap: 8 }}>
              {fabricList.map(f => <ViewCard key={f.id} item={f} type="fabric" />)}
            </div>
            <p style={{ fontSize: 10, color: T.textGhost, margin: "8px 0 0" }}>💡 Escolha o tecido na etapa <strong>Personalizar</strong></p>
          </div>

          {/* Shields — view only */}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.textFaint, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 10 }}>
              <Ic.shield /> Escudos disponíveis
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(100px,1fr))", gap: 8 }}>
              {shieldList.map(s => <ViewCard key={s.id} item={s} type="shield" />)}
            </div>
            <p style={{ fontSize: 10, color: T.textGhost, margin: "8px 0 0" }}>💡 Escolha o escudo na etapa <strong>Personalizar</strong></p>
          </div>

          {/* Price */}
          <div style={{ background: "#e9456011", border: "1px solid #e9456033", borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 3 }}>Preço unitário (10–50 unidades)</div>
            <div style={{ fontSize: 18, color: "#e94560", fontWeight: 900 }}>R$ {model.price.toFixed(2).replace(".", ",")}</div>
            <div style={{ fontSize: 10, color: T.textGhost, marginTop: 3 }}>* Escudo bordado aumenta o valor.</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 18px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
          <button onClick={onClose} style={{ ...sBtnP() }}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

// ─── DESIGN PREVIEW FULLSCREEN ───────────────────────────────────────────────
function DesignPreviewOverlay({ item, cc, T, onSelect, onClose, isSelected }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "#000", zIndex: 1200, display: "flex", flexDirection: "column" }}
      onClick={onClose}
    >
      {/* Image — fills screen */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }} onClick={e => e.stopPropagation()}>
        {item.img
          ? <img src={item.img} alt={item.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} />
          : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke={cc + "66"} strokeWidth="1.2"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              <span style={{ fontSize: 14, color: "rgba(255,255,255,.4)" }}>Sem imagem</span>
            </div>
        }
        {/* Close button top-right */}
        <button
          onClick={onClose}
          style={{ position: "absolute", top: 16, right: 16, background: "rgba(0,0,0,.6)", border: "1px solid rgba(255,255,255,.15)", borderRadius: "50%", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", backdropFilter: "blur(4px)" }}
        >
          <Ic.x />
        </button>
      </div>

      {/* Bottom bar — name + buttons */}
      <div
        style={{ flexShrink: 0, background: "rgba(0,0,0,.85)", backdropFilter: "blur(12px)", borderTop: "1px solid rgba(255,255,255,.1)", padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
        onClick={e => e.stopPropagation()}
      >
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#fff" }}>{item.name}</div>
          {item.tags && item.tags.length > 0 && item.tags[0] !== "todos" && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
              {item.tags.map(t => (
                <span key={t} style={{ fontSize: 10, background: `${cc}33`, border: `1px solid ${cc}55`, borderRadius: 4, padding: "1px 7px", color: cc, fontWeight: 600 }}>{t}</span>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.15)", borderRadius: 10, color: "#fff", padding: "10px 20px", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
            Voltar
          </button>
          <button
            onClick={() => { onSelect(isSelected ? null : item.id, isSelected ? null : item); }}
            style={{ background: isSelected ? "rgba(255,255,255,.1)" : `linear-gradient(135deg,${cc},${cc}cc)`, border: isSelected ? "1px solid rgba(255,255,255,.2)" : "none", borderRadius: 10, color: isSelected ? "rgba(255,255,255,.7)" : "#fff", padding: "10px 20px", cursor: "pointer", fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
          >
            {isSelected ? "Remover" : <><Ic.check /> Selecionar</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── RESOLVE DESIGNS (lazy, called on modal open) ────────────────────────────
function resolveDesignsForModel(modelPath) {
  if (!modelPath) return [];
  const dataArray = window.dataArray || [];
  return dataArray
    .filter(item => (item.sport_id || "").includes(modelPath))
    .map(item => ({
      id: String(item.id || item.sport_id),
      name: item.name || item.nome || item.title || `Design ${item.id}`,
      img: item.svg_thumb_path
        ? `https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/svgs-thumbs/${item.svg_thumb_path}`
        : null,
      path: item.sport_id || null,
      tags: item.tags ? (Array.isArray(item.tags) ? item.tags : [item.tags]) : [],
    }));
}

// ─── DESIGNS MODAL ────────────────────────────────────────────────────────────
function DesignsModal({ model, selectedDesign, onSelect, onClose }) {
  const T = useT();
  const cc = CAT_COLOR[model.category] || "#888";
  const [search, setSearch] = useState("");
  const [hoveredDesign, setHoveredDesign] = useState(null);
  const [previewDesign, setPreviewDesign] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const mobile = window.innerWidth <= 700;

  // Lazy load — resolve designs only when modal opens
  useEffect(() => {
    // Use pre-populated designs if available, otherwise resolve from dataArray
    const resolved = (model.designs && model.designs.length > 0)
      ? model.designs
      : resolveDesignsForModel(model.path || "");
    setDesigns(resolved);
    setLoading(false);
  }, []);
  const filtered = designs.filter(d => d.name.toLowerCase().includes(search.toLowerCase()));
  const selName = designs.find(d => d.id === selectedDesign)?.name;
  const hovItem = hoveredDesign ? designs.find(d => d.id === hoveredDesign) : null;
  const previewItem = previewDesign ? designs.find(d => d.id === previewDesign) : null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000c", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: mobile ? 0 : 12 }} onClick={onClose}>
      <div
        style={{
          background: T.surface, border: mobile ? "none" : `1.5px solid ${T.border3}`,
          borderRadius: mobile ? 0 : 20,
          width: "100%", maxWidth: mobile ? "100%" : 700,
          height: mobile ? "100%" : "auto", maxHeight: mobile ? "100%" : "92vh",
          display: "flex", gap: 0, overflow: "hidden",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Grid panel */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <div style={{ padding: "14px 18px 10px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div>
                <h3 style={{ color: T.text, fontWeight: 800, fontSize: 15, margin: 0 }}>Designs — {model.name}</h3>
                <p style={{ color: T.textFaint, fontSize: 11, margin: "3px 0 0" }}>
                  Clique para ampliar · confirme para selecionar
                </p>
              </div>
              <button onClick={onClose} style={{ background: T.surface2, border: "none", borderRadius: 9, color: T.textMuted, padding: "6px 8px", cursor: "pointer", display: "flex", flexShrink: 0 }}><Ic.x /></button>
            </div>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: T.textFaint, pointerEvents: "none" }}><Ic.srch /></span>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar design..." style={{ ...sInp(T), paddingLeft: 28 }} />
            </div>
          </div>

          {/* Selected badge */}
          {selName && (
            <div style={{ padding: "8px 18px 0", flexShrink: 0 }}>
              <span style={{ fontSize: 11, color: cc, fontWeight: 700, background: `${cc}18`, borderRadius: 6, padding: "4px 10px", border: `1px solid ${cc}44` }}>
                ✓ Design selecionado: {selName}
              </span>
            </div>
          )}

          {/* Grid */}
          <div style={{ overflowY: "auto", flex: 1, padding: "12px 18px" }}>
            {loading && (
              <div style={{ textAlign: "center", padding: 40, color: T.textFaint, fontSize: 13 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>Carregando designs...
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div style={{ textAlign: "center", padding: 40, color: T.textGhost, fontSize: 13 }}>Nenhum design encontrado</div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px,1fr))", gap: 12 }}>
              {filtered.map(d => {
                const isSel = selectedDesign === d.id;
                return (
                  <div
                    key={d.id}
                    style={{
                      background: isSel ? `${cc}18` : T.surface2,
                      border: isSel ? `2px solid ${cc}` : `1.5px solid ${T.border}`,
                      borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column",
                      boxShadow: isSel ? `0 0 0 1px ${cc}44, 0 4px 20px ${cc}33` : "none",
                      transition: "all .15s", position: "relative",
                    }}
                  >
                    {/* Selected badge */}
                    {isSel && (
                      <div style={{ position: "absolute", top: 8, right: 8, width: 20, height: 20, borderRadius: "50%", background: cc, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2, boxShadow: `0 2px 8px ${cc}88` }}>
                        <Ic.check />
                      </div>
                    )}
                    {/* Thumb */}
                    <div style={{ width: "100%", aspectRatio: "1", overflow: "hidden", background: T.surface3 }}>
                      {d.img
                        ? <img src={d.img} alt={d.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        : <MediaPlaceholder label="Design" cc={cc} T={T} />
                      }
                    </div>
                    {/* Name */}
                    <div style={{ padding: "6px 8px 4px", fontSize: 11, fontWeight: isSel ? 700 : 500, color: isSel ? cc : T.textSub, textAlign: "center" }}>
                      {d.name}
                    </div>
                    {/* Action buttons */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5, padding: "4px 8px 8px" }}>
                      <button
                        onClick={() => onSelect(isSel ? null : d.id, isSel ? null : d)}
                        style={{
                          background: isSel ? `${cc}22` : T.surface3,
                          border: `1px solid ${isSel ? cc : T.border3}`,
                          borderRadius: 7, padding: "5px 4px", cursor: "pointer",
                          fontSize: 10, fontWeight: 700, color: isSel ? cc : T.textMuted,
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
                        }}
                      >
                        <Ic.check /> {isSel ? "Remover" : "Selecionar"}
                      </button>
                      <button
                        onClick={() => setPreviewDesign(d.id)}
                        style={{
                          background: T.surface3, border: `1px solid ${T.border3}`,
                          borderRadius: 7, padding: "5px 4px", cursor: "pointer",
                          fontSize: 10, fontWeight: 600, color: T.textMuted,
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 3,
                        }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                        Ampliar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: "12px 18px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, gap: 10 }}>
            <div style={{ fontSize: 12, color: T.textFaint }}>{filtered.length} design{filtered.length !== 1 ? "s" : ""} disponíve{filtered.length !== 1 ? "is" : "l"}</div>
            <button onClick={onClose} style={{ ...sBtnP() }}>Confirmar</button>
          </div>
        </div>
      </div>

      {/* Fullscreen preview overlay */}
      {previewItem && (
        <DesignPreviewOverlay
          item={previewItem}
          cc={cc}
          T={T}
          isSelected={selectedDesign === previewItem.id}
          onSelect={(id, item) => { onSelect(id, item); setPreviewDesign(null); }}
          onClose={() => setPreviewDesign(null)}
        />
      )}
    </div>
  );
}

// ─── STEP BAR ────────────────────────────────────────────────────────────────
function StepBar({ current }) {
  const T = useT();
  const steps = [{ n: 1, l: "Perfil" }, { n: 2, l: "Modelagens" }, { n: 3, l: "Detalhes" }];
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0 }}>
      {steps.map((s, i) => (
        <div key={s.n} style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: current === s.n ? "#e94560" : current > s.n ? "#2a1520" : T.surface2, border: current === s.n ? "2px solid #e94560" : current > s.n ? "2px solid #e9456055" : `2px solid ${T.border2}`, color: current === s.n ? "#fff" : current > s.n ? "#e94560" : T.textFaint, fontSize: 10, fontWeight: 700, boxShadow: current === s.n ? "0 0 14px #e9456055" : "none", transition: "all .3s" }}>
              {current > s.n ? <Ic.check /> : s.n}
            </div>
            <span className="ks-step-label" style={{ fontSize: 8, color: current >= s.n ? T.textMuted : T.textGhost, fontWeight: current === s.n ? 700 : 400, letterSpacing: .8, textTransform: "uppercase" }}>{s.l}</span>
          </div>
          {i < 2 && <div style={{ width: 32, height: 2, background: current > s.n ? "#e9456033" : T.border, margin: "0 4px", marginBottom: 14, transition: "background .3s" }} />}
        </div>
      ))}
    </div>
  );
}

// ─── STEP 1 ──────────────────────────────────────────────────────────────────
function Step1({ profile, setProfile, onNext }) {
  const T = useT();
  const [type, setType] = useState(profile.type || "");
  const [name, setName] = useState(profile.name || "");
  const [site, setSite] = useState(profile.site || "");
  const [ig, setIg] = useState(profile.instagram || "");
  const [fb, setFb] = useState(profile.facebook || "");
  const ok = type && name.trim();
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        <h2 style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 2 }}>Qual é o seu perfil?</h2>
        <p style={{ color: T.textFaint, fontSize: 12, marginBottom: 16 }}>Selecione e preencha os dados da sua organização</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px,1fr))", gap: 8, marginBottom: 16 }}>
          {PROFILE_TYPES.map(p => (
            <button key={p.id} onClick={() => setType(p.id)} style={{ background: type === p.id ? "#e9456011" : T.surface2, border: type === p.id ? "1.5px solid #e94560" : `1.5px solid ${T.border2}`, borderRadius: 11, padding: "11px 12px", cursor: "pointer", textAlign: "left", position: "relative", transition: "all .2s" }}>
              {type === p.id && <span style={{ position: "absolute", top: 6, right: 6, background: "#e94560", borderRadius: "50%", width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}><Ic.check /></span>}
              <div style={{ fontWeight: 700, color: type === p.id ? "#e94560" : T.textSub, fontSize: 13, marginBottom: 2 }}>{p.label}</div>
              <div style={{ fontSize: 11, color: T.textFaint }}>{p.desc}</div>
            </button>
          ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div><label style={sLbl(T)}>Nome *</label><input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Atlético da Vila" style={sInp(T)} /></div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px,1fr))", gap: 9 }}>
            {[["Site", site, setSite, "https://..."], ["Instagram", ig, setIg, "@usuario"], ["Facebook", fb, setFb, "@pagina"]].map(([l, v, fn, ph]) => (
              <div key={l}><label style={sLbl(T)}>{l}</label><input value={v} onChange={e => fn(e.target.value)} placeholder={ph} style={sInp(T)} /></div>
            ))}
          </div>
        </div>
      </div>{/* end scrollable */}
      {/* Sticky nav */}
      <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 0, paddingTop: 14, display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
        <button onClick={() => { setProfile({ type, name, site, instagram: ig, facebook: fb }); onNext(); }} disabled={!ok} style={{ ...sBtnP(), opacity: ok ? 1 : 0.4, cursor: ok ? "pointer" : "not-allowed" }}>Próximo: Escolher Modelagens <Ic.cR /></button>
      </div>
    </div>
  );
}

// ─── CART ITEM ───────────────────────────────────────────────────────────────
function CartItem({ model, qty, onQty, onRemove }) {
  const T = useT(); const cc = CAT_COLOR[model.category] || "#888";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", background: T.surface2, borderRadius: 10, border: `1px solid ${T.border}` }}>
      <ModelThumb model={model} size={44} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, color: T.text, fontSize: 13 }}>{model.name}</span>
          <span style={{ fontSize: 9, background: `${cc}22`, color: cc, borderRadius: 4, padding: "1px 5px", fontWeight: 700, textTransform: "uppercase" }}>{model.category}</span>
        </div>
        <div style={{ fontSize: 11, color: cc, fontWeight: 700, marginTop: 1 }}>R$ {(model.price * qty).toFixed(2).replace(".", ",")} <span style={{ color: T.textFaint, fontWeight: 400 }}>({qty} un.)</span></div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
        <button onClick={() => onQty(Math.max(1, qty - 1))} style={{ background: T.surface3, border: `1px solid ${cc}44`, borderRadius: 6, color: cc, width: 24, height: 24, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
        <input type="number" min={1} max={9999} value={qty} onChange={e => onQty(Math.max(1, parseInt(e.target.value) || 1))} style={{ width: 40, background: T.surface3, border: `1px solid ${T.border3}`, borderRadius: 6, color: T.text, textAlign: "center", padding: "3px 2px", fontSize: 12, fontWeight: 700 }} />
        <button onClick={() => onQty(qty + 1)} style={{ background: T.surface3, border: `1px solid ${cc}44`, borderRadius: 6, color: cc, width: 24, height: 24, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
        <button onClick={onRemove} style={{ background: T.surface3, border: "1px solid #e9456033", borderRadius: 6, color: "#e94560", width: 24, height: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", marginLeft: 2 }}><Ic.trash /></button>
      </div>
    </div>
  );
}

// ─── MODEL CARD (with info button + selection highlight) ─────────────────────
function ModelCard({ model, qty, onAdd, onRemove, onQty }) {
  const T = useT();
  const [infoOpen, setInfoOpen] = useState(false);
  const [expandOpen, setExpandOpen] = useState(false);
  const sel = qty > 0;
  const cc = CAT_COLOR[model.category] || "#888";
  const specs = [
    model.sizes && { icon: "📐", k: "Tamanho", v: model.sizes },
    model.collar && { icon: "👕", k: "Gola", v: model.collar },
    model.sleeve && { icon: "💪", k: "Manga", v: model.sleeve },
    model.fabric && { icon: "🧵", k: "Tecido", v: model.fabric },
    model.shield && { icon: "🛡️", k: "Escudo", v: model.shield },
  ].filter(Boolean);

  // Selection highlight — strong and unmistakable
  const cardBg = sel ? (T.mode === "dark" ? `linear-gradient(135deg, ${cc}38 0%, ${cc}18 100%)` : `linear-gradient(135deg, ${cc}22 0%, ${cc}0c 100%)`) : T.surface;
  const cardBord = sel ? `2px solid ${cc}` : `1.5px solid ${T.border}`;

  return (
    <>
      <div style={{ background: cardBg, border: cardBord, borderRadius: 12, boxShadow: sel ? `0 0 0 1px ${cc}88, 0 6px 24px ${cc}55` : "none", display: "flex", overflow: "hidden", transition: "all .25s", position: "relative" }}>
        {/* Selected left accent bar */}
        {sel && <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: cc, borderRadius: "12px 0 0 12px" }} />}

        <div style={{ position: "relative", flexShrink: 0, marginLeft: sel ? 4 : 0 }}>
          <ModelThumb model={model} size={isMobile ? 80 : 140} />
          {sel && <div style={{ position: "absolute", top: 5, left: 5, width: 20, height: 20, borderRadius: "50%", background: cc, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 12px ${cc}, 0 0 0 2px #fff4` }}><Ic.check /></div>}
        </div>

        <div style={{ flex: 1, minWidth: 0, padding: "8px 10px 8px 8px", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 6, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 1 }}>
                <span style={{ fontWeight: 800, color: T.text, fontSize: 13 }}>{model.name}</span>
                {(model.categories && model.categories.length > 0 ? model.categories : [model.category]).map(cat => (
                  <span key={cat} style={{ fontSize: 9, background: `${(CAT_COLOR[cat] || cc)}22`, color: CAT_COLOR[cat] || cc, borderRadius: 4, padding: "1px 5px", fontWeight: 700, textTransform: "uppercase", flexShrink: 0 }}>{cat}</span>
                ))}
                {sel && <span style={{ fontSize: 9, background: `${cc}22`, color: cc, borderRadius: 4, padding: "1px 6px", fontWeight: 700, flexShrink: 0 }}>✓ Selecionado</span>}
              </div>
              {model.extras && <div style={{ fontSize: 10, color: T.textFaint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: isMobile ? 120 : 240 }}>✦ {model.extras}</div>}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <span style={{ fontWeight: 800, color: cc, fontSize: 13 }}>R$ {model.price.toFixed(2).replace(".", ",")}</span>
              {/* EXPAND BUTTON */}
              <button
                onClick={e => { e.stopPropagation(); setExpandOpen(true); }}
                title="Ver imagem ampliada"
                style={{ background: T.surface3, border: `1px solid ${T.border3}`, borderRadius: 7, color: T.textMuted, padding: "4px 6px", cursor: "pointer", display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, transition: "all .15s", flexShrink: 0 }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
              </button>
              {/* INFO BUTTON */}
              <button
                onClick={e => { e.stopPropagation(); setInfoOpen(true); }}
                title="Mais informações"
                style={{ background: T.surface3, border: `1px solid ${T.border3}`, borderRadius: 7, color: T.textMuted, padding: "4px 6px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, transition: "all .15s", flexShrink: 0 }}
              >
                <Ic.info /> <span style={{ fontSize: 10 }}>Info</span>
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
            {specs.map((s, i) => (
              <span key={i} style={{
                fontSize: 10,
                background: sel ? `${cc}18` : T.surface2,
                border: `1.5px solid ${sel ? `${cc}44` : T.border2}`,
                borderRadius: 6, padding: "3px 8px",
                color: sel ? T.textSub : T.textMuted,
                fontWeight: 600,
                display: "flex", alignItems: "center", gap: 4,
                maxWidth: isMobile ? 120 : 220, overflow: "hidden",
              }}>
                <span style={{ fontSize: 11, flexShrink: 0 }}>{s.icon}</span>
                <span style={{ color: sel ? cc : T.textFaint, fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: .5, flexShrink: 0 }}>{s.k}</span>
                <span style={{ color: sel ? T.textSub : T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.v}</span>
              </span>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {!sel
              ? <button onClick={onAdd} style={{ ...sBtnP(), fontSize: 11, padding: "5px 11px", background: cc, borderRadius: 7 }}><Ic.cart /> Adicionar</button>
              : <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button onClick={onRemove} style={{ background: T.surface3, border: `1px solid ${cc}44`, borderRadius: 6, color: cc, width: 24, height: 24, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
                <input type="number" min={1} max={9999} value={qty} onChange={e => onQty(Math.max(1, parseInt(e.target.value) || 1))} style={{ width: 40, background: T.surface3, border: `1px solid ${T.border3}`, borderRadius: 6, color: T.text, textAlign: "center", padding: "3px 1px", fontSize: 12, fontWeight: 700 }} />
                <button onClick={() => onQty(qty + 1)} style={{ background: T.surface3, border: `1px solid ${cc}44`, borderRadius: 6, color: cc, width: 24, height: 24, cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
                <span style={{ fontSize: 10, color: T.textFaint }}>= <span style={{ color: cc, fontWeight: 700 }}>R$ {(model.price * qty).toFixed(2).replace(".", ",")}</span></span>
              </div>
            }
          </div>
        </div>
      </div>
      {infoOpen && <ModelInfoModal model={model} onClose={() => setInfoOpen(false)} />}
      {expandOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "#000d", zIndex: 2000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}
          onClick={() => setExpandOpen(false)}
        >
          {/* Close */}
          <button
            onClick={() => setExpandOpen(false)}
            style={{ position: "absolute", top: 16, right: 16, background: "#fff2", border: "none", borderRadius: "50%", width: 40, height: 40, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}
          >
            <Ic.x />
          </button>
          {/* Image */}
          <div
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "min(90vw, 520px)", maxHeight: "80vh", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}
          >
            <ModelThumb model={model} size={Math.min(window.innerWidth * 0.8, 480)} />
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", marginBottom: 4 }}>{model.name}</div>
              <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
                {(model.categories?.length ? model.categories : [model.category]).map(cat => (
                  <span key={cat} style={{ fontSize: 11, background: `${CAT_COLOR[cat] || cc}33`, color: CAT_COLOR[cat] || cc, borderRadius: 5, padding: "2px 9px", fontWeight: 700, textTransform: "uppercase" }}>{cat}</span>
                ))}
              </div>
              {model.description && <p style={{ fontSize: 13, color: "rgba(255,255,255,.7)", marginTop: 8, maxWidth: 420, lineHeight: 1.5 }}>{model.description}</p>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── STEP 2 ──────────────────────────────────────────────────────────────────
function Step2({ cart, setCart, onNext, onBack }) {
  const T = useT();
  const MODELS = useModels(); // 👈 dados dinâmicos do banco
  const [q, setQ] = useState(""); const [cat, setCat] = useState("Todos"); const [cartOpen, setCartOpen] = useState(false);
  // Derive unique categories from loaded models (flattened from categories array)
  const dynamicCats = useMemo(() => {
    const seen = new Set();
    MODELS
      .filter(m => m.path && (window.dataArray || []).some(item => (item.sport_id || "").includes(m.path)))
      .forEach(m => (m.categories || [m.category]).forEach(c => c && seen.add(c)));
    return ["Todos", ...Array.from(seen)];
  }, [MODELS]);
  // Reset filter if selected category disappears after models reload
  useEffect(() => {
    if (cat !== "Todos" && !dynamicCats.includes(cat)) setCat("Todos");
  }, [dynamicCats]);
  const filtered = useMemo(
    () =>
      MODELS.filter(
        (m) =>
          // só exibe se tiver designs no dataArray
          m.path && (window.dataArray || []).some(item => (item.sport_id || "").includes(m.path)) &&
          (cat === "Todos" || (m.categories || [m.category]).includes(cat)) &&
          (m.name ?? "").toLowerCase().includes((q ?? "").toLowerCase())
      ),
    [cat, q, MODELS]
  );
  const totalQty = Object.values(cart).reduce((a, b) => a + b, 0);
  const totalPrice = MODELS.reduce((s, m) => s + (cart[m.id] || 0) * m.price, 0);
  const selItems = MODELS.filter(m => cart[m.id] > 0);
  const addC = id => setCart(c => ({ ...c, [id]: c[id] || 1 }));
  const remC = id => setCart(c => { const n = { ...c }; if ((n[id] || 0) <= 1) delete n[id]; else n[id]--; return n; });
  const setQ2 = (id, v) => setCart(c => ({ ...c, [id]: v }));
  const delC = id => setCart(c => { const n = { ...c }; delete n[id]; return n; });
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 0 }}>
      {/* Sub-header */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 10, flexShrink: 0 }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 2 }}>Escolha as Modelagens</h2>
          <p style={{ color: T.textFaint, fontSize: 12, margin: 0 }}>Clique em <span style={{ color: T.textMuted }}>Info</span> para ver tecido e escudo</p>
        </div>
        <button onClick={() => setCartOpen(true)} style={{ background: totalQty > 0 ? "#e9456011" : T.surface2, border: totalQty > 0 ? "1.5px solid #e9456055" : `1.5px solid ${T.border2}`, borderRadius: 10, padding: "7px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "all .2s" }}>
          <Ic.cart />
          <span style={{ color: T.textSub, fontSize: 13, fontWeight: 600 }}>Carrinho</span>
          {totalQty > 0 && <><span style={{ background: "#e94560", color: "#fff", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>{totalQty}</span><span style={{ color: "#e94560", fontWeight: 800, fontSize: 13 }}>R$ {totalPrice.toFixed(2).replace(".", ",")}</span></>}
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 8, alignItems: "center", flexShrink: 0 }}>
        <div style={{ position: "relative", flex: "1 1 150px", minWidth: 120 }}>
          <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: T.textFaint, pointerEvents: "none" }}><Ic.srch /></span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar..." style={{ ...sInp(T), paddingLeft: 28 }} />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {dynamicCats.map(c => {
            const isActive = cat === c;
            const color = c === "Todos" ? "#e94560" : (CAT_COLOR[c] || "#e94560");
            return (
              <button key={c} onClick={() => setCat(c)} style={{ background: isActive ? color : T.surface2, border: isActive ? "1px solid transparent" : `1px solid ${T.border}`, borderRadius: 7, padding: "4px 10px", color: isActive ? "#fff" : T.textMuted, fontSize: 11, fontWeight: 600, cursor: "pointer", transition: "all .15s", whiteSpace: "nowrap" }}>
                {c}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scrollable list — fills remaining height */}
      <div style={{
        flex: 1, overflowY: "auto", border: `1px solid ${T.border}`, borderRadius: 12, padding: "6px", background: T.listBg, minHeight: 0, maxHeight: isMobile
          ? "calc(100vh - 440px)"
          : "calc(100vh - 360px)"
      }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 6,
        }}>
          {filtered.length === 0 && <div style={{ textAlign: "center", padding: 50, color: T.textGhost, fontSize: 13 }}>Nenhuma modelagem encontrada</div>}
          {filtered.map(m => <ModelCard key={m.id} model={m} qty={cart[m.id] || 0} onAdd={() => addC(m.id)} onRemove={() => remC(m.id)} onQty={v => setQ2(m.id, v)} />)}
        </div>
      </div>

      {/* Pinned nav */}
      <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 8, paddingTop: 12, display: "flex", justifyContent: "space-between", flexShrink: 0 }}>
        <button onClick={onBack} style={sBtnG(T)}><Ic.cL /> Voltar</button>
        <button onClick={onNext} disabled={totalQty === 0} style={{ ...sBtnP(), opacity: totalQty > 0 ? 1 : 0.4, cursor: totalQty > 0 ? "pointer" : "not-allowed" }}>Próximo: Personalizar <Ic.cR /></button>
      </div>

      {/* Cart drawer */}
      {cartOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#000b", zIndex: 900, display: "flex", justifyContent: "flex-end" }} onClick={() => setCartOpen(false)}>
          <div style={{ background: T.surface, borderLeft: `1.5px solid ${T.border}`, width: "100%", maxWidth: 400, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "16px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <div><h3 style={{ color: T.text, fontWeight: 800, fontSize: 16, margin: 0 }}>Carrinho</h3><p style={{ color: T.textFaint, fontSize: 11, margin: "2px 0 0" }}>{totalQty} {totalQty === 1 ? "item" : "itens"}</p></div>
              <button onClick={() => setCartOpen(false)} style={{ background: T.surface2, border: "none", borderRadius: 8, color: T.textMuted, padding: "5px 7px", cursor: "pointer", display: "flex" }}><Ic.x /></button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px" }}>
              {selItems.length === 0
                ? <div style={{ textAlign: "center", padding: 50, color: T.textGhost, fontSize: 13 }}><div style={{ fontSize: 32, marginBottom: 8 }}>🛒</div>Nenhum item adicionado</div>
                : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{selItems.map(m => <CartItem key={m.id} model={m} qty={cart[m.id]} onQty={v => setQ2(m.id, v)} onRemove={() => delC(m.id)} />)}</div>
              }
            </div>
            {totalQty > 0 && (
              <div style={{ padding: "14px 18px", borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ color: T.textMuted, fontSize: 13 }}>Total estimado</span>
                  <span style={{ color: "#e94560", fontWeight: 800, fontSize: 18 }}>R$ {totalPrice.toFixed(2).replace(".", ",")}</span>
                </div>
                <button onClick={() => { setCartOpen(false); onNext(); }} style={{ ...sBtnP(), width: "100%", justifyContent: "center" }}>Próximo: Personalizar <Ic.cR /></button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FONT PICKER ─────────────────────────────────────────────────────────────
function FontPickerModal({ previewText, currentFont, onSelect, onClose }) {
  const T = useT();
  const [search, setSearch] = useState(""); const [group, setGroup] = useState("Todos");
  const [loaded, setLoaded] = useState(false); const [temp, setTemp] = useState(currentFont || "");
  const displayText = previewText?.trim() || "ATLETICO FC";
  useEffect(() => {
    if (!document.getElementById("gfonts-link")) { const l = document.createElement("link"); l.id = "gfonts-link"; l.rel = "stylesheet"; l.href = GFONTS_URL; l.onload = () => setLoaded(true); document.head.appendChild(l); }
    else setLoaded(true);
  }, []);
  const groups = useMemo(() => { const ql = search.toLowerCase(); if (group === "Todos") return [{ label: "Todos", fonts: ALL_FONTS.filter(f => f.toLowerCase().includes(ql)) }]; const g = FONT_GROUPS.find(x => x.label === group); return [{ ...g, fonts: g.fonts.filter(f => f.toLowerCase().includes(ql)) }]; }, [search, group]);
  return (
    <div style={{ position: "fixed", inset: 0, background: "#000c", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }} onClick={onClose}>
      <div style={{ background: T.surface, border: `1.5px solid ${T.border3}`, borderRadius: 18, width: "100%", maxWidth: 660, maxHeight: "84vh", display: "flex", flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "14px 16px 10px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
            <h3 style={{ color: T.text, fontWeight: 800, fontSize: 15, margin: 0 }}>Escolher Fonte</h3>
            <button onClick={onClose} style={{ background: T.surface2, border: "none", borderRadius: 7, color: T.textMuted, padding: "4px 6px", cursor: "pointer", display: "flex" }}><Ic.x /></button>
          </div>
          <div style={{ position: "relative", marginBottom: 7 }}>
            <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: T.textFaint, pointerEvents: "none" }}><Ic.srch /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar fonte..." autoFocus style={{ ...sInp(T), paddingLeft: 28 }} />
          </div>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {["Todos", ...FONT_GROUPS.map(g => g.label)].map(g => (
              <button key={g} onClick={() => setGroup(g)} style={{ background: group === g ? "#e94560" : T.surface2, border: group === g ? "1px solid #e94560" : `1px solid ${T.border}`, borderRadius: 5, padding: "3px 8px", color: group === g ? "#fff" : T.textMuted, fontSize: 10, fontWeight: 600, cursor: "pointer" }}>{g}</button>
            ))}
          </div>
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "9px 13px" }}>
          {!loaded && <div style={{ textAlign: "center", padding: 40, color: T.textFaint, fontSize: 13 }}>Carregando fontes...</div>}
          {loaded && groups.map(g => (
            <div key={g.label}>
              {g.fonts.length === 0 && <div style={{ color: T.textGhost, fontSize: 12, padding: "12px 0" }}>Nenhuma encontrada</div>}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(175px,1fr))", gap: 6 }}>
                {g.fonts.map(font => {
                  const isSel = temp === font; return (
                    <button key={font} onClick={() => setTemp(font)} style={{ background: isSel ? "#e9456011" : T.surface2, border: isSel ? "1.5px solid #e94560" : `1.5px solid ${T.border}`, borderRadius: 8, padding: "8px 10px", cursor: "pointer", textAlign: "left", position: "relative" }}>
                      {isSel && <span style={{ position: "absolute", top: 5, right: 5, background: "#e94560", borderRadius: "50%", width: 13, height: 13, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}><Ic.check /></span>}
                      <div style={{ fontSize: 8, color: T.textFaint, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4, fontFamily: "sans-serif" }}>{font}</div>
                      <div style={{ fontFamily: `'${font}',sans-serif`, fontSize: displayText.length > 12 ? 14 : 18, color: isSel ? "#e94560" : T.textSub, lineHeight: 1.1, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayText}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, color: T.textFaint }}>{temp ? <span>Selecionada: <span style={{ color: "#e94560", fontFamily: `'${temp}',sans-serif`, fontWeight: 700, fontSize: 13 }}>{temp}</span></span> : "Nenhuma selecionada"}</div>
          <div style={{ display: "flex", gap: 7 }}>
            <button onClick={onClose} style={sBtnG(T)}>Cancelar</button>
            <button onClick={() => { onSelect(temp); onClose(); }} disabled={!temp} style={{ ...sBtnP(), opacity: temp ? 1 : 0.4, cursor: temp ? "pointer" : "not-allowed" }}>Usar <Ic.cR /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FontPickerField({ label, textValue, onTextChange, fontValue, onFontChange, placeholder }) {
  const T = useT(); const [open, setOpen] = useState(false);
  return (
    <div>
      <label style={sLbl(T)}>{label}</label>
      <div style={{ display: "flex", gap: 5 }}>
        <div style={{ flex: 1, position: "relative" }}>
          <input value={textValue} onChange={e => onTextChange(e.target.value)} placeholder={placeholder || "Digite..."} style={{ ...sInp(T), paddingRight: fontValue ? 110 : 13, fontFamily: fontValue ? `'${fontValue}',sans-serif` : "inherit", fontSize: fontValue ? 14 : 13, fontWeight: fontValue ? 700 : 400 }} />
          {fontValue && <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 9, color: T.textFaint, background: T.surface3, borderRadius: 4, padding: "2px 5px", maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "sans-serif" }}>{fontValue}</span>}
        </div>
        <button onClick={() => setOpen(true)} style={{ background: fontValue ? "#e9456011" : T.surface2, border: fontValue ? "1.5px solid #e9456055" : `1.5px solid ${T.border2}`, borderRadius: 9, color: fontValue ? "#e94560" : T.textMuted, padding: "0 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, flexShrink: 0, height: 37 }}>
          <Ic.font />{fontValue ? "Trocar" : "Fonte"}
        </button>
      </div>
      {open && <FontPickerModal previewText={textValue} currentFont={fontValue} onSelect={onFontChange} onClose={() => setOpen(false)} />}
    </div>
  );
}

// ─── SPONSOR UPLOAD ──────────────────────────────────────────────────────────
function SponsorUpload({ sponsors, onChange }) {
  const T = useT(); const ref = useRef();
  const handleFiles = e => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    // Read all files in parallel, then append all at once to avoid stale state
    let loaded = 0;
    const newItems = new Array(files.length);
    files.forEach((f, i) => {
      const r = new FileReader();
      r.onload = ev => {
        newItems[i] = { id: nid(), name: f.name, url: ev.target.result };
        loaded++;
        if (loaded === files.length) {
          onChange(p => [...p, ...newItems]);
        }
      };
      r.readAsDataURL(f);
    });
    e.target.value = "";
  };
  return (
    <div>
      <label style={sLbl(T)}>Patrocinadores — logos</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 4 }}>
        {sponsors.map(s => (
          <div key={s.id} style={{ position: "relative", width: 52, height: 52, borderRadius: 8, overflow: "hidden", border: `1px solid ${T.border3}`, background: T.surface2, flexShrink: 0 }}>
            <img src={s.url} alt={s.name} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            <button onClick={() => onChange(p => p.filter(x => x.id !== s.id))} style={{ position: "absolute", top: 1, right: 1, background: "#000b", border: "none", borderRadius: "50%", color: "#fff", width: 13, height: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}><Ic.x /></button>
          </div>
        ))}
        <button onClick={() => ref.current.click()} style={{ width: 52, height: 52, borderRadius: 8, border: `1.5px dashed ${T.border3}`, background: T.surface2, color: T.textFaint, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, fontSize: 9, flexShrink: 0 }}>
          <Ic.img /><span>Add logo</span>
        </button>
      </div>
      <input ref={ref} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleFiles} />
    </div>
  );
}


// ─── COLOR PICKER MODAL ───────────────────────────────────────────────────────
// Espelha exatamente o resolveTableColors do color-picker.tsx
function getSimulatorColors() {
  const allTables = window.colors ?? [];
  const dataShared = window.dataShared;

  if (!allTables.length) return [];

  // 1. mainColorTable
  const mainId = dataShared?.mainColorTable;
  if (mainId) {
    const table = allTables.find(t => t.id == mainId); // == para tolerar string/number
    if (table?.colors?.length) return table.colors.map(c => c.hex).filter(Boolean);
  }

  // 2. primeira tabela disponível
  if (allTables[0]?.colors?.length) return allTables[0].colors.map(c => c.hex).filter(Boolean);

  return [];
}

function ColorPickerModal({ label, currentColor, onSelect, onClose }) {
  const T = useT();
  const [hex, setHex] = useState(currentColor || "");
  const [hexInput, setHexInput] = useState(currentColor || "");
  const [hovered, setHovered] = useState(null);
  // Lê no momento da montagem — window.colors já está pronto quando o usuário abre o modal
  const [tableColors] = useState(() => getSimulatorColors());

  const isValidHex = v => /^#[0-9A-Fa-f]{6}$/.test(v);
  const handleHexChange = v => { setHexInput(v); if (isValidHex(v)) setHex(v); };
  const confirm = (col) => { onSelect(col || hex); onClose(); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000c", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }} onClick={onClose}>
      <div style={{ background: T.surface, border: `1.5px solid ${T.border3}`, borderRadius: 20, width: "100%", maxWidth: 480, maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "14px 18px 10px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <h3 style={{ color: T.text, fontWeight: 800, fontSize: 15, margin: 0 }}>Escolher Cor — {label}</h3>
          <button onClick={onClose} style={{ background: T.surface2, border: "none", borderRadius: 9, color: T.textMuted, padding: "6px 8px", cursor: "pointer", display: "flex" }}><Ic.x /></button>
        </div>

        {/* Flat grid — mesmo layout do color-picker.tsx */}
        <div style={{ overflowY: "auto", flex: 1, padding: "14px 18px" }}>
          {tableColors.length === 0 ? (
            <div style={{ color: T.textFaint, fontSize: 13, textAlign: "center", padding: "24px 0" }}>
              Tabela de cores não configurada
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {tableColors.map(col => {
                const isSel = hex === col;
                const isHov = hovered === col;
                const r = parseInt(col.slice(1,3),16)||0, g = parseInt(col.slice(3,5),16)||0, b = parseInt(col.slice(5,7),16)||0;
                const isLight = (r*.299+g*.587+b*.114) > 186;
                return (
                  <button
                    key={col}
                    onClick={() => { setHex(col); setHexInput(col); }}
                    onMouseEnter={() => setHovered(col)}
                    onMouseLeave={() => setHovered(null)}
                    title={col}
                    style={{
                      width: 34, height: 34, borderRadius: 9, background: col, cursor: "pointer", outline: "none", flexShrink: 0,
                      border: isSel ? "3px solid #fff" : isLight ? `2px solid ${T.border3}` : isHov ? "2px solid #ffffffcc" : "2px solid transparent",
                      boxShadow: isSel ? `0 0 0 2px ${col}, 0 0 12px ${col}88` : isHov ? `0 0 0 1px ${col}88, 0 4px 12px ${col}66` : "none",
                      transform: isHov ? "scale(1.15)" : isSel ? "scale(1.1)" : "scale(1)",
                      transition: "all .12s",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    {isSel && <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 8l3.5 3.5L13 5" stroke={isLight?"#333":"#fff"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer: hex input + preview + confirm */}
        <div style={{ padding: "12px 18px", borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0, flexWrap: "wrap" }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: isValidHex(hex) ? hex : "transparent", border: `2px solid ${T.border3}`, flexShrink: 0, transition: "background .15s" }} />
          <div style={{ flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 10, color: T.textFaint, marginBottom: 3, textTransform: "uppercase", letterSpacing: .8 }}>Código HEX</div>
            <input
              value={hexInput}
              onChange={e => handleHexChange(e.target.value.startsWith("#") ? e.target.value : "#" + e.target.value)}
              placeholder="#e94560"
              maxLength={7}
              style={{ ...sInp(T), fontFamily: "monospace", fontSize: 14, fontWeight: 700, letterSpacing: 1, color: isValidHex(hexInput) ? T.text : "#e94560" }}
            />
          </div>
          <div style={{ display: "flex", gap: 7, flexShrink: 0 }}>
            {currentColor && <button onClick={() => confirm("")} style={{ ...sBtnG(T), fontSize: 12 }}>Remover</button>}
            <button onClick={() => confirm(isValidHex(hexInput) ? hexInput : hex)} disabled={!isValidHex(hex) && !isValidHex(hexInput)} style={{ ...sBtnP(), opacity: (isValidHex(hex) || isValidHex(hexInput)) ? 1 : 0.4 }}>Usar esta cor</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── PERSONALIZE CARD ────────────────────────────────────────────────────────
function PersonalizeCard({ model, qty, c, onChange, onDuplicate, onDelete, isDuplicate, isOpen, onToggleOpen, hideHeader = false }) {
  const T = useT();
  const open = isOpen;
  const setOpen = () => onToggleOpen();
  const [activeTab, setActiveTab] = useState("design");
  const [designOpen, setDesignOpen] = useState(false);
  const [fabricOpen, setFabricOpen] = useState(false);
  const [shieldOpen, setShieldOpen] = useState(false);
  const [colorPickerKey, setColorPickerKey] = useState(null);
  const [selDesignItem, setSelDesignItem] = useState(null);

  useEffect(() => {
    if (!c.design) { setSelDesignItem(null); return; }
    const cached = (model.designs || []).find(d => d.id === c.design);
    if (cached) { setSelDesignItem(cached); return; }
    const all = resolveDesignsForModel(model.path || "");
    const found = all.find(d => d.id === c.design);
    setSelDesignItem(found || null);
  }, [c.design]);

  const cc  = CAT_COLOR[model.category] || "#e94560";
  const set = (k, v) => onChange({ ...c, [k]: v });

  const specs = [
    model.sizes  && { icon: "📐", label: "Tamanho", val: model.sizes },
    model.collar && { icon: "👕", label: "Gola",    val: model.collar },
    model.sleeve && { icon: "💪", label: "Manga",   val: model.sleeve },
  ].filter(Boolean);

  // ── Tab definitions ──────────────────────────────────────────────────────
  const hasDesigns = model.path && (window.dataArray || []).some(i => (i.sport_id || "").includes(model.path));
  const hasFabrics = model.fabrics && model.fabrics.length > 0;
  const hasShields = model.shields && model.shields.length > 0;

  const TABS = [
    hasDesigns && { id: "design", icon: "🎨", label: "Design",  done: !!c.design },
    hasFabrics && { id: "fabric", icon: "🧵", label: "Tecido",  done: !!c.fabric },
    hasShields && { id: "shield", icon: "🛡️", label: "Escudo",  done: !!c.shield },
                  { id: "cores",  icon: "🎨", label: "Cores",   done: !!(c.primaryColor || c.secondaryColor) },
                  { id: "textos", icon: "✍️", label: "Textos",  done: !!(c.teamName || c.number || c.backText) },
                  { id: "logos",  icon: "📎", label: "Logos",   done: (c.sponsors || []).length > 0 },
  ].filter(Boolean);

  // ensure activeTab is valid
  const tabIds = TABS.map(t => t.id);
  const safeTab = tabIds.includes(activeTab) ? activeTab : tabIds[0];

  return (
    <div style={{
      background: hideHeader ? "transparent" : T.surface,
      border: hideHeader ? "none" : open ? `2px solid ${cc}` : `1.5px solid ${T.border}`,
      borderRadius: hideHeader ? 0 : 14,
      boxShadow: hideHeader ? "none" : open ? `0 0 0 1px ${cc}22, 0 8px 28px ${cc}18` : "none",
      transition: "all .22s", overflow: "hidden",
    }}>
      {/* Accent top bar — hidden when embedded in right panel */}
      <div style={{ height: hideHeader ? 0 : 3, background: open ? `linear-gradient(90deg,${cc},${cc}66)` : T.border, transition: "background .3s" }} />

      {/* ── Card header ─────────────────────────────────────────────────── */}
      <div style={{ padding: "10px 13px", display: "flex", alignItems: "center", gap: 10 }}>
        {/* Thumb */}
        <ModelThumb model={model} size={isMobile ? 48 : 64} />

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
            <input
              value={c.customName || ""}
              onChange={e => set("customName", e.target.value)}
              onClick={e => e.stopPropagation()}
              placeholder={model.name}
              style={{ background: "transparent", border: "none", outline: "none", fontWeight: 700, color: T.text, fontSize: 13, fontFamily: "inherit", minWidth: 60, maxWidth: 160, cursor: "text", borderBottom: c.customName ? `1.5px solid ${cc}66` : `1px dashed ${T.border3}`, paddingBottom: 1 }}
            />
            {isDuplicate && <span style={{ fontSize: 9, background: `${cc}22`, color: cc, borderRadius: 4, padding: "1px 6px", fontWeight: 700, border: `1px solid ${cc}44` }}>cópia</span>}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4 }}>
            {(model.categories?.length ? model.categories : [model.category]).map(cat => (
              <span key={cat} style={{ fontSize: 9, background: `${CAT_COLOR[cat] || cc}22`, color: CAT_COLOR[cat] || cc, borderRadius: 4, padding: "1px 6px", fontWeight: 700, textTransform: "uppercase" }}>{cat}</span>
            ))}
            <span style={{ fontSize: 10, color: T.textFaint }}>{qty} un. · R$ {(model.price * qty).toFixed(2).replace(".",",")}</span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
          <button onClick={e => { e.stopPropagation(); onDuplicate(); }} title="Duplicar" style={{ background: T.surface2, border: `1px solid ${T.border3}`, borderRadius: 7, color: T.textMuted, padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 3, fontSize: 10 }}>
            <Ic.copy />{!isMobile && <span>Duplicar</span>}
          </button>
          {isDuplicate && (
            <button onClick={e => { e.stopPropagation(); onDelete(); }} title="Remover" style={{ background: "#e9456011", border: "1px solid #e9456033", borderRadius: 7, color: "#e94560", padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center", gap: 3, fontSize: 10 }}>
              <Ic.trash2 />{!isMobile && <span>Remover</span>}
            </button>
          )}
          <button onClick={() => setOpen()} style={{ background: open ? cc : T.surface2, border: open ? "none" : `1.5px solid ${T.border2}`, borderRadius: 8, color: open ? "#fff" : T.textMuted, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, boxShadow: open ? `0 2px 10px ${cc}55` : "none", transition: "all .2s" }}>
            <span style={{ display: "inline-block", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}><Ic.cD /></span>
            {!isMobile && <span>{open ? "Fechar" : "Personalizar"}</span>}
          </button>
        </div>
      </div>

      {/* ── Progress dots (visible when closed) ─────────────────────────── */}
      {!open && (
        <div style={{ padding: "0 13px 10px", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TABS.map(t => (
            <span key={t.id} title={t.label} style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10, color: t.done ? "#22c55e" : T.textFaint, background: t.done ? "#22c55e15" : T.surface2, border: `1px solid ${t.done ? "#22c55e44" : T.border}`, borderRadius: 20, padding: "2px 8px" }}>
              {t.done ? "✓" : "○"} {t.label}
            </span>
          ))}
        </div>
      )}

      {/* ── Expanded: tab bar + tab content ────────────────────────── */}
      {open && (
        <div style={{ borderTop: `1px solid ${cc}22` }}>
          {/* Tab bar */}
          <div style={{ display: "flex", overflowX: "auto", padding: "0 13px", gap: 2, borderBottom: `1px solid ${T.border}` }}>
            {TABS.map(t => (
              <button key={t.id} title={t.label} onClick={() => setActiveTab(t.id)} style={{
                flexShrink: 0,
                padding: isMobile ? "8px 10px" : "9px 12px",
                fontSize: isMobile ? 10 : 11,
                fontWeight: safeTab === t.id ? 700 : 500,
                color: safeTab === t.id ? cc : T.textMuted,
                background: "none", border: "none",
                borderBottom: safeTab === t.id ? `2px solid ${cc}` : "2px solid transparent",
                cursor: "pointer", display: "flex", alignItems: "center",
                gap: isMobile ? 3 : 5,
                transition: "all .15s", marginBottom: -1,
              }}>
                <span style={{ fontSize: isMobile ? 15 : 13 }}>{t.icon}</span>
                {!isMobile && t.label}
                {t.done && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e", flexShrink: 0 }} />}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ padding: isMobile ? "12px 11px" : "14px 15px" }}>

            {/* DESIGN */}
            {safeTab === "design" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <p style={{ fontSize: 12, color: T.textFaint, margin: 0 }}>Escolha o design base desta modelagem</p>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", background: T.surface2, border: `1.5px solid ${selDesignItem ? "#e94560" : T.border2}`, borderRadius: 10, padding: "10px 14px", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {selDesignItem?.img
                      ? <img src={selDesignItem.img} alt={selDesignItem.name} style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 8, border: "1.5px solid #e9456066" }} />
                      : <div style={{ width: 44, height: 44, borderRadius: 8, background: T.surface3, border: `1.5px solid ${T.border3}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🎨</div>
                    }
                    <div>
                      <div style={{ fontSize: 10, color: T.textFaint, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Design selecionado</div>
                      <div style={{ fontSize: 13, color: selDesignItem ? T.text : T.textFaint, fontWeight: selDesignItem ? 700 : 400 }}>{selDesignItem ? selDesignItem.name : "Nenhum"}</div>
                    </div>
                  </div>
                  <button onClick={() => setDesignOpen(true)} style={{ background: selDesignItem ? "#e9456018" : cc, border: "none", borderRadius: 9, color: selDesignItem ? "#e94560" : "#fff", padding: "8px 16px", cursor: "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {selDesignItem ? "Trocar" : "Ver Designs"}
                  </button>
                </div>
                {selDesignItem && (
                  <button onClick={() => { set("design", null); setSelDesignItem(null); }} style={{ ...sBtnG(T), fontSize: 11, alignSelf: "flex-start" }}>Remover design</button>
                )}
              </div>
            )}

            {/* FABRIC */}
            {safeTab === "fabric" && (() => {
              const selFabric = model.fabrics.find(f => f.id === c.fabric);
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <p style={{ fontSize: 12, color: T.textFaint, margin: 0 }}>Escolha o tecido para esta modelagem</p>
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? "80px" : "100px"},1fr))`, gap: 8 }}>
                    {model.fabrics.map(f => (
                      <OptionCard key={f.id} item={f} selected={c.fabric || null} onSelect={id => set("fabric", id === c.fabric ? null : id)} cc={cc} T={T} type="fabric" />
                    ))}
                  </div>
                  {selFabric && <p style={{ fontSize: 11, color: "#22c55e", margin: 0 }}>✓ {selFabric.name}</p>}
                </div>
              );
            })()}

            {/* SHIELD */}
            {safeTab === "shield" && (() => {
              const selShield = model.shields.find(s => s.id === c.shield);
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <p style={{ fontSize: 12, color: T.textFaint, margin: 0 }}>Escolha o escudo para esta modelagem</p>
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? "80px" : "100px"},1fr))`, gap: 8 }}>
                    {model.shields.map(s => (
                      <OptionCard key={s.id} item={s} selected={c.shield || null} onSelect={id => set("shield", id === c.shield ? null : id)} cc={cc} T={T} type="shield" />
                    ))}
                  </div>
                  {selShield && <p style={{ fontSize: 11, color: "#22c55e", margin: 0 }}>✓ {selShield.name}</p>}
                </div>
              );
            })()}

            {/* CORES */}
            {safeTab === "cores" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {[["Cor Principal", "primaryColor"], ["Cor Secundária", "secondaryColor"]].map(([label, key]) => (
                  <div key={key}>
                    <label style={{ ...sLbl(T), marginBottom: 8 }}>{label}</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        onClick={() => setColorPickerKey(key)}
                        style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, background: T.surface2, border: `1.5px solid ${T.border3}`, borderRadius: 10, padding: "8px 12px", cursor: "pointer" }}
                      >
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: c[key] || T.surface3, border: `2px solid ${T.border3}`, flexShrink: 0, boxShadow: c[key] ? `0 0 8px ${c[key]}66` : "none" }} />
                        <span style={{ fontSize: 12, color: c[key] ? T.text : T.textFaint, fontWeight: 600, flex: 1, textAlign: "left" }}>
                          {c[key] ? c[key].toUpperCase() : "Selecionar cor…"}
                        </span>
                        <span style={{ fontSize: 10, color: T.textFaint }}>▼</span>
                      </button>
                      {c[key] && (
                        <button onClick={() => set(key, "")} style={{ width: 32, height: 32, borderRadius: 8, background: T.surface3, border: `1px solid ${T.border3}`, cursor: "pointer", color: T.textMuted, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Ic.x />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {colorPickerKey && (
              <ColorPickerModal
                label={colorPickerKey === "primaryColor" ? "Cor Principal" : "Cor Secundária"}
                currentColor={c[colorPickerKey] || ""}
                onSelect={v => { set(colorPickerKey, v); setColorPickerKey(null); }}
                onClose={() => setColorPickerKey(null)}
              />
            )}

            {/* TEXTOS */}
            {safeTab === "textos" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <FontPickerField label="Nome do Time" textValue={c.teamName || ""} onTextChange={v => set("teamName", v)} fontValue={c.teamNameFont || ""} onFontChange={v => set("teamNameFont", v)} placeholder="Ex: ATLETICO FC" />
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 9 }}>
                  <FontPickerField label="Numeração #numero" textValue={c.number || ""} onTextChange={v => set("number", v)} fontValue={c.numberFont || ""} onFontChange={v => set("numberFont", v)} placeholder="Ex: 1–22" />
                  <FontPickerField label="Sobrenome / Costas" textValue={c.backText || ""} onTextChange={v => set("backText", v)} fontValue={c.backTextFont || ""} onFontChange={v => set("backTextFont", v)} placeholder="Ex: sobrenomes" />
                </div>
                <div>
                  <label style={sLbl(T)}>Observações</label>
                  <textarea value={c.notes || ""} onChange={e => set("notes", e.target.value)} placeholder="Referências, estilo, elementos..." style={{ ...sInp(T), minHeight: 52, resize: "vertical" }} />
                </div>
              </div>
            )}

            {/* LOGOS */}
            {safeTab === "logos" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <p style={{ fontSize: 12, color: T.textFaint, margin: 0 }}>Adicione logos de patrocinadores ou escudo do time</p>
                <SponsorUpload sponsors={c.sponsors || []} onChange={s => set("sponsors", typeof s === "function" ? s(c.sponsors || []) : s)} />
              </div>
            )}

          </div>
        </div>
      )}

      {/* Design modal — outside open block so it stays mounted */}
      {designOpen && (
        <DesignsModal
          model={model}
          selectedDesign={c.design || null}
          onSelect={(id, item) => { set("design", id); setSelDesignItem(item || null); setDesignOpen(false); }}
          onClose={() => setDesignOpen(false)}
        />
      )}
    </div>
  );
}

// ─── STEP 2 UNIFIED — grid de cards + drawer lateral ────────────────────────
function Step2Unified({ cart, setCart, customizations, setCustomizations, entries, setEntries, onNext, onBack, onCanNextChange }) {
  const T = useT();
  const MODELS = useModels();
  const [q, setQ]                   = useState("");
  const [cat, setCat]               = useState("Todos");
  const [activeEntryId, setActiveEntryId] = useState(null);
  const [drawerTab, setDrawerTab]   = useState("personalizar");

  // ── Categories ─────────────────────────────────────────────────────────────
  const dynamicCats = useMemo(() => {
    const seen = new Set();
    MODELS
      .filter(m => m.path && (window.dataArray||[]).some(i => (i.sport_id||"").includes(m.path)))
      .forEach(m => (m.categories||[m.category]).forEach(c => c && seen.add(c)));
    return ["Todos", ...Array.from(seen)];
  }, [MODELS]);
  useEffect(() => { if (cat !== "Todos" && !dynamicCats.includes(cat)) setCat("Todos"); }, [dynamicCats]);

  const filtered = useMemo(() => MODELS.filter(m =>
    m.path && (window.dataArray||[]).some(i => (i.sport_id||"").includes(m.path)) &&
    (cat === "Todos" || (m.categories||[m.category]).includes(cat)) &&
    (m.name??"").toLowerCase().includes((q??"").toLowerCase())
  ), [cat, q, MODELS]);

  // ── Cart ───────────────────────────────────────────────────────────────────
  const addC  = id => setCart(c => ({...c, [id]: c[id]||1}));
  const remC  = id => setCart(c => { const n={...c}; if((n[id]||0)<=1) delete n[id]; else n[id]--; return n; });
  const setQC = (id, v) => setCart(c => ({...c, [id]: Math.max(1,v)}));
  const totalQty   = Object.values(cart).reduce((a,b)=>a+b,0);
  const totalPrice = MODELS.reduce((s,m)=>s+(cart[m.id]||0)*m.price,0);

  // ── Customizations ─────────────────────────────────────────────────────────
  const getCustom = id => customizations[id]||{};
  const setCustom = (id,v) => setCustomizations(cs=>({...cs,[id]:v}));

  const ensureEntry = modelId => {
    const base = String(modelId);
    if (!entries.find(e => e.entryId===base))
      setEntries(prev=>[...prev,{entryId:base,modelId}]);
    return base;
  };
  const duplicate = (entryId, modelId) => {
    const newId = `${modelId}_${nid()}`;
    setCustomizations(cs=>({...cs,[newId]:{...getCustom(entryId)}}));
    setEntries(prev => {
      const idx = prev.findIndex(e=>e.entryId===entryId);
      const next=[...prev]; next.splice(idx+1,0,{entryId:newId,modelId}); return next;
    });
    setActiveEntryId(newId);
  };
  const deleteEntry = entryId => {
    setEntries(prev=>prev.filter(e=>e.entryId!==entryId));
    setCustomizations(cs=>{const n={...cs};delete n[entryId];return n;});
    if (activeEntryId===entryId) setActiveEntryId(null);
  };

  const openDrawer = (modelId, tab="personalizar") => {
    const id = ensureEntry(modelId);
    setActiveEntryId(id);
    setDrawerTab(tab);
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!onCanNextChange) return;
    const missing = entries.filter(({entryId,modelId}) => {
      const m = MODELS.find(x=>x.id===modelId);
      if (!m) return false;
      const hasD = m.path && (window.dataArray||[]).some(i=>(i.sport_id||"").includes(m.path));
      return hasD && !getCustom(entryId).design;
    });
    onCanNextChange(missing.length===0, missing);
  }, [entries, customizations, MODELS]);

  const activeEntry = activeEntryId ? entries.find(e=>e.entryId===activeEntryId) : null;
  const activeModel = activeEntry ? MODELS.find(m=>m.id===activeEntry.modelId) : null;
  const acc         = activeModel ? (CAT_COLOR[activeModel.category]||"#e94560") : "#e94560";

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%"}}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:12,marginBottom:14,flexWrap:"wrap"}}>
        <div style={{flex:1}}>
          <h2 style={{fontSize:17,fontWeight:800,color:T.text,margin:0}}>Escolha as Modelagens</h2>
          <p style={{color:T.textFaint,fontSize:12,margin:"2px 0 0"}}>Adicione e personalize cada modelo do seu pedido</p>
        </div>
        {totalQty > 0 && (
          <div style={{background:T.surface2,border:`1px solid ${T.border}`,borderRadius:10,padding:"6px 14px",display:"flex",alignItems:"center",gap:8,fontSize:12}}>
            <Ic.cart />
            <span style={{fontWeight:700,color:T.text}}>{totalQty} {totalQty===1?"modelagem":"modelagens"}</span>
            <span style={{color:"#e94560",fontWeight:800}}>R$ {totalPrice.toFixed(2).replace(".",",")}</span>
          </div>
        )}
      </div>

      {/* ── Search + category filters ────────────────────────────────────── */}
      <div style={{flexShrink:0,display:"flex",flexWrap:"wrap",gap:8,marginBottom:14,alignItems:"center"}}>
        <div style={{position:"relative",flex:"1 1 160px",minWidth:120}}>
          <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",color:T.textFaint,pointerEvents:"none"}}><Ic.srch /></span>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar..." style={{...sInp(T),paddingLeft:28}} />
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
          {dynamicCats.map(c => {
            const active = cat===c;
            const col = c==="Todos" ? "#e94560" : (CAT_COLOR[c]||"#e94560");
            return <button key={c} onClick={()=>setCat(c)} style={{background:active?col:T.surface2,border:active?"1px solid transparent":`1px solid ${T.border}`,borderRadius:20,padding:"5px 14px",color:active?"#fff":T.textMuted,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>{c}</button>;
          })}
        </div>
      </div>

      {/* ── Body: cards grid + right drawer ─────────────────────────────── */}
      <div style={{flex:1,minHeight:0,display:"flex",gap:14,overflow:"hidden"}}>

        {/* GRID */}
        <div style={{flex:1,minWidth:0,overflowY:"auto",paddingRight:2}}>
          {filtered.length===0 && <div style={{textAlign:"center",padding:60,color:T.textGhost,fontSize:13}}>Nenhuma modelagem encontrada</div>}
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(190px,1fr))",gap:14}}>
            {filtered.map(m => {
              const cc  = CAT_COLOR[m.category]||"#e94560";
              const qty = cart[m.id]||0;
              const sel = qty > 0;
              const modelEntries = entries.filter(e=>e.modelId===m.id);
              const hasD = m.path && (window.dataArray||[]).some(i=>(i.sport_id||"").includes(m.path));

              return (
                <div key={m.id} style={{
                  background:T.surface,
                  border: sel ? `1.5px solid ${cc}` : `1px solid ${T.border}`,
                  borderRadius:16, overflow:"hidden", display:"flex", flexDirection:"column",
                  boxShadow: sel ? `0 4px 20px ${cc}22` : `0 1px 6px ${T.shadow}`,
                  transition:"all .2s",
                }}>
                  {/* Top accent bar */}
                  <div style={{height:3,background:sel?cc:T.border,transition:"background .2s"}} />

                  {/* Thumb area */}
                  <div
                    onClick={()=>{ if(sel) openDrawer(m.id,"personalizar"); else { addC(m.id); openDrawer(m.id,"personalizar"); }}}
                    style={{background:sel?`${cc}08`:T.surface2,display:"flex",justifyContent:"center",alignItems:"center",padding:"24px 16px 16px",cursor:"pointer",position:"relative"}}
                  >
                    {sel && (
                      <div style={{position:"absolute",top:8,right:8,width:22,height:22,background:cc,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 2px 8px ${cc}66`}}>
                        <Ic.check />
                      </div>
                    )}
                    <ModelThumb model={m} size={130} />
                  </div>

                  {/* Info */}
                  <div style={{padding:"12px 14px",flex:1,display:"flex",flexDirection:"column",gap:6}}>
                    <div style={{fontWeight:800,fontSize:14,color:T.text,lineHeight:1.2}}>{m.name}</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:3}}>
                      {(m.categories?.length?m.categories:[m.category]).map(cat=>(
                        <span key={cat} style={{fontSize:9,background:`${CAT_COLOR[cat]||cc}18`,color:CAT_COLOR[cat]||cc,borderRadius:4,padding:"1px 6px",fontWeight:700,textTransform:"uppercase"}}>{cat}</span>
                      ))}
                    </div>
                    <div style={{fontSize:15,fontWeight:800,color:cc}}>R$ {m.price.toFixed(2).replace(".",",")}</div>

                    {/* Sub-entries list (when duplicated) */}
                    {sel && modelEntries.length > 0 && (
                      <div style={{display:"flex",flexDirection:"column",gap:4,marginTop:2}}>
                        {modelEntries.map(entry=>{
                          const c = getCustom(entry.entryId);
                          const isDup = entry.entryId!==String(m.id);
                          const isAct = activeEntryId===entry.entryId;
                          const needsDesign = hasD && !c.design;
                          return (
                            <div key={entry.entryId}
                              onClick={()=>{setActiveEntryId(entry.entryId);setDrawerTab("personalizar");}}
                              style={{display:"flex",alignItems:"center",gap:7,background:isAct?`${cc}10`:T.surface2,border:isAct?`1px solid ${cc}`:`1px solid ${T.border}`,borderRadius:8,padding:"6px 10px",cursor:"pointer",transition:"all .15s"}}
                            >
                              <div style={{width:7,height:7,borderRadius:"50%",background:needsDesign?"#f59e0b":"#22c55e",flexShrink:0}} />
                              <span style={{fontSize:11,color:T.textSub,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                {c.customName||(isDup?`${m.name} (cópia)`:m.name)}
                              </span>
                              {isDup && <button onClick={e=>{e.stopPropagation();deleteEntry(entry.entryId);}} style={{background:"none",border:"none",color:T.textFaint,cursor:"pointer",padding:0,display:"flex"}}><Ic.x /></button>}
                            </div>
                          );
                        })}
                        {/* Add copy */}
                        <button onClick={()=>duplicate(modelEntries[0].entryId,m.id)} style={{background:"none",border:`1px dashed ${T.border2}`,borderRadius:8,padding:"5px",cursor:"pointer",fontSize:10,color:T.textFaint,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                          <Ic.copy /> Adicionar cópia
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div style={{padding:"0 14px 14px",display:"flex",flexDirection:"column",gap:8}}>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7}}>
                      <button onClick={()=>openDrawer(m.id,"info")} style={{background:T.surface2,border:`1px solid ${T.border}`,borderRadius:9,padding:"8px 6px",cursor:"pointer",fontSize:11,fontWeight:600,color:T.textMuted,display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                        <Ic.info /> Info
                      </button>
                      {!sel
                        ? <button onClick={()=>{addC(m.id);openDrawer(m.id,"personalizar");}} style={{background:cc,border:"none",borderRadius:9,padding:"8px 6px",cursor:"pointer",fontSize:11,fontWeight:700,color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                            <Ic.cart /> Adicionar
                          </button>
                        : <button onClick={()=>openDrawer(m.id,"personalizar")} style={{background:`${cc}15`,border:`1.5px solid ${cc}44`,borderRadius:9,padding:"8px 6px",cursor:"pointer",fontSize:11,fontWeight:700,color:cc,display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                            ✏️ Personalizar
                          </button>
                      }
                    </div>
                    {/* Qty controls */}
                    {sel && (
                      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                        <button onClick={()=>{if(qty<=1){setCart(c=>{const n={...c};delete n[m.id];return n;});setEntries(p=>p.filter(e=>e.modelId!==m.id));if(activeEntry?.modelId===m.id)setActiveEntryId(null);}else remC(m.id);}} style={{background:T.surface2,border:`1px solid ${cc}44`,borderRadius:6,color:cc,width:28,height:28,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                        <input type="number" min={1} max={9999} value={qty} onChange={e=>setQC(m.id,parseInt(e.target.value)||1)} style={{width:44,background:T.surface2,border:`1px solid ${T.border}`,borderRadius:6,color:T.text,textAlign:"center",padding:"4px 2px",fontSize:13,fontWeight:700}} />
                        <button onClick={()=>setQC(m.id,qty+1)} style={{background:T.surface2,border:`1px solid ${cc}44`,borderRadius:6,color:cc,width:28,height:28,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                        <span style={{fontSize:11,color:cc,fontWeight:700}}>R$ {(m.price*qty).toFixed(2).replace(".",",")}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT DRAWER */}
        {!isMobile && (
          activeModel ? (
            <div style={{width:600,flexShrink:0,display:"flex",flexDirection:"column",background:T.surface,border:`1px solid ${T.border}`,borderRadius:16,overflow:"hidden",boxShadow:`0 4px 24px ${T.shadow}`}}>
              {/* Drawer header */}
              <div style={{padding:"14px 16px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                <ModelThumb model={activeModel} size={44} />
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:800,fontSize:13,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{getCustom(activeEntry.entryId).customName||activeModel.name}</div>
                  <div style={{fontSize:11,color:T.textFaint}}>{cart[activeEntry.modelId]} un. · R$ {(activeModel.price*cart[activeEntry.modelId]).toFixed(2).replace(".",",")}</div>
                </div>
                <div style={{display:"flex",gap:5}}>
                  <button onClick={()=>duplicate(activeEntry.entryId,activeEntry.modelId)} title="Duplicar" style={{background:T.surface2,border:`1px solid ${T.border}`,borderRadius:7,color:T.textMuted,padding:"5px 7px",cursor:"pointer",display:"flex",alignItems:"center"}}><Ic.copy /></button>
                  {activeEntry.entryId!==String(activeEntry.modelId)&&<button onClick={()=>deleteEntry(activeEntry.entryId)} title="Remover cópia" style={{background:"#e9456011",border:"1px solid #e9456033",borderRadius:7,color:"#e94560",padding:"5px 7px",cursor:"pointer",display:"flex",alignItems:"center"}}><Ic.trash2 /></button>}
                  <button onClick={()=>setActiveEntryId(null)} style={{background:T.surface2,border:`1px solid ${T.border}`,borderRadius:7,color:T.textMuted,padding:"5px 7px",cursor:"pointer",display:"flex",alignItems:"center"}}><Ic.x /></button>
                </div>
              </div>
              {/* Tabs */}
              <div style={{display:"flex",borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
                {[{id:"personalizar",label:"✏️ Personalizar"},{id:"info",label:"ℹ️ Informações"}].map(tab=>(
                  <button key={tab.id} onClick={()=>setDrawerTab(tab.id)} style={{flex:1,padding:"10px",fontSize:12,fontWeight:drawerTab===tab.id?700:500,color:drawerTab===tab.id?acc:T.textMuted,background:"none",border:"none",borderBottom:drawerTab===tab.id?`2px solid ${acc}`:"2px solid transparent",cursor:"pointer",marginBottom:-1,transition:"all .15s"}}>{tab.label}</button>
                ))}
              </div>
              {/* Content */}
              <div style={{flex:1,overflowY:"auto",minHeight:0}}>
                {drawerTab==="info" && (
                  <div style={{padding:16}}>
                    {/* Model specs inline */}
                    {[
                      activeModel.sizes  && {icon:"📐",k:"Tamanho",v:activeModel.sizes},
                      activeModel.collar && {icon:"👕",k:"Gola",v:activeModel.collar},
                      activeModel.sleeve && {icon:"💪",k:"Manga",v:activeModel.sleeve},
                      activeModel.fabric && {icon:"🧵",k:"Tecido",v:activeModel.fabric},
                      activeModel.shield && {icon:"🛡️",k:"Escudo",v:activeModel.shield},
                      activeModel.extras && {icon:"✦",k:"Extras",v:activeModel.extras},
                    ].filter(Boolean).map(s=>(
                      <div key={s.k} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
                        <span style={{fontSize:18,width:24,textAlign:"center",flexShrink:0}}>{s.icon}</span>
                        <div>
                          <div style={{fontSize:9,color:T.textFaint,textTransform:"uppercase",letterSpacing:1,marginBottom:2}}>{s.k}</div>
                          <div style={{fontSize:13,fontWeight:600,color:T.text}}>{s.v}</div>
                        </div>
                      </div>
                    ))}
                    {activeModel.description && (
                      <div style={{marginTop:12,padding:12,background:T.surface2,borderRadius:10,fontSize:12,color:T.textSub,lineHeight:1.6}}>{activeModel.description}</div>
                    )}
                    <div style={{marginTop:16,padding:12,background:"#e9456010",border:"1px solid #e9456030",borderRadius:10}}>
                      <div style={{fontSize:10,color:T.textFaint,marginBottom:4}}>Preço (10–50 un.)</div>
                      <div style={{fontSize:20,fontWeight:900,color:"#e94560"}}>R$ {activeModel.price.toFixed(2).replace(".",",")}</div>
                    </div>
                  </div>
                )}
                {drawerTab==="personalizar" && (
                  <PersonalizeCard
                    key={activeEntry.entryId}
                    model={activeModel}
                    qty={cart[activeEntry.modelId]}
                    c={getCustom(activeEntry.entryId)}
                    onChange={v=>setCustom(activeEntry.entryId,v)}
                    onDuplicate={()=>duplicate(activeEntry.entryId,activeEntry.modelId)}
                    onDelete={()=>deleteEntry(activeEntry.entryId)}
                    isDuplicate={activeEntry.entryId!==String(activeEntry.modelId)}
                    isOpen={true}
                    onToggleOpen={()=>{}}
                    hideHeader={true}
                  />
                )}
              </div>
            </div>
          ) : (
            <div style={{width:360,flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,border:`1px dashed ${T.border2}`,borderRadius:16,color:T.textFaint}}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={T.border3} strokeWidth="1"><path d="M20.38 3.46L16 2a4 4 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.57a1 1 0 00.99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.84l.58-3.57a2 2 0 00-1.34-2.23z"/></svg>
              <div style={{textAlign:"center",padding:"0 20px"}}>
                <div style={{fontWeight:600,color:T.textMuted,fontSize:13,marginBottom:4}}>Nenhuma modelagem selecionada</div>
                <div style={{fontSize:11,color:T.textFaint,lineHeight:1.5}}>Clique em "Adicionar" ou "Personalizar" em um card para começar</div>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}


// ─── STEP 3 ──────────────────────────────────────────────────────────────────
// Each "entry" = { entryId: unique string, modelId: number }
// Original cart entries use entryId = String(modelId). Duplicates get a nid()-based entryId.
function Step3({ cart, customizations, setCustomizations, entries, setEntries, onNext, onBack, cartBtn, onCanNextChange }) {
  const T = useT();
  const MODELS = useModels(); // 👈 adicione essa linha
  const baseSelected = MODELS.filter(m => cart[m.id] > 0);

  // Initialise entries from cart when first entering Step3 (if entries is empty)
  useEffect(() => {
    if (entries.length === 0) {
      setEntries(baseSelected.map(m => ({ entryId: String(m.id), modelId: m.id })));
    }
  }, []);

  // Also sync if cart changes (user went back and changed selection)
  const cartKey = baseSelected.map(m => m.id).join(",");
  useEffect(() => {
    setEntries(prev => {
      // Keep existing entries whose modelId is still in cart, add new ones
      const inCart = new Set(baseSelected.map(m => m.id));
      const filtered = prev.filter(e => inCart.has(e.modelId));
      const existingModelIds = new Set(filtered.map(e => e.modelId));
      const newEntries = baseSelected.filter(m => !existingModelIds.has(m.id)).map(m => ({ entryId: String(m.id), modelId: m.id }));
      return [...filtered, ...newEntries];
    });
  }, [cartKey]);

  const [openEntryId, setOpenEntryId] = useState(null);
  const getCustom = entryId => customizations[entryId] || {};
  const setCustom = (entryId, v) => setCustomizations(cs => ({ ...cs, [entryId]: v }));

  const duplicate = (entryId, modelId) => {
    const newEntryId = `${modelId}_${nid()}`;
    // Copy current customization into the new slot
    const copy = { ...getCustom(entryId) };
    setCustomizations(cs => ({ ...cs, [newEntryId]: copy }));
    setEntries(prev => {
      const idx = prev.findIndex(e => e.entryId === entryId);
      const next = [...prev];
      next.splice(idx + 1, 0, { entryId: newEntryId, modelId });
      return next;
    });
  };

  const deleteEntry = entryId => {
    setEntries(prev => prev.filter(e => e.entryId !== entryId));
    setCustomizations(cs => { const n = { ...cs }; delete n[entryId]; return n; });
  };

  // Count how many times each modelId appears in entries (for "isDuplicate" badge)
  const modelCount = {};
  entries.forEach(e => { modelCount[e.modelId] = (modelCount[e.modelId] || 0) + 1; });

  // Notify App of canNext state changes (effect, not during render)
  useEffect(() => {
    if (!onCanNextChange) return;
    const missing = entries.filter(({ entryId, modelId }) => {
      const model = MODELS.find(m => m.id === modelId);
      if (!model) return false;
      const hasDesigns = model.path && (window.dataArray || []).some(item => (item.sport_id || "").includes(model.path));
      if (!hasDesigns) return false;
      return !(customizations[entryId] || {}).design;
    });
    onCanNextChange(missing.length === 0, missing);
  }, [entries, customizations, MODELS]);

  // Selected entry for the detail panel (desktop)
  const activeEntry = openEntryId
    ? entries.find(e => e.entryId === openEntryId)
    : null;
  const activeModel = activeEntry ? MODELS.find(m => m.id === activeEntry.modelId) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <div style={{ flexShrink: 0, marginBottom: 12, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 2 }}>Personalizar Modelagens</h2>
          <p style={{ color: T.textFaint, fontSize: 12, margin: 0 }}>Selecione uma modelagem para personalizar</p>
        </div>
        {cartBtn}
      </div>

      {/* ── Two-panel layout (desktop) / single list (mobile) ── */}
      <div style={{ flex: 1, minHeight: 0, display: "flex", gap: 12, overflow: "hidden" }}>

        {/* LEFT — model cards list */}
        <div style={{ width: isMobile ? "100%" : 260, flexShrink: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, paddingRight: isMobile ? 0 : 4, paddingBottom: 8 }}>
          {entries.map(({ entryId, modelId }) => {
            const model = MODELS.find(m => m.id === modelId);
            if (!model) return null;
            const isDuplicate = entryId !== String(modelId);
            const cc = CAT_COLOR[model.category] || "#e94560";
            const c = getCustom(entryId);
            const isActive = openEntryId === entryId;

            // Completion indicators
            const hasDesigns = model.path && (window.dataArray || []).some(i => (i.sport_id || "").includes(model.path));
            const done = {
              design:  !hasDesigns || !!c.design,
              fabric:  !(model.fabrics?.length) || !!c.fabric,
              shield:  !(model.shields?.length) || !!c.shield,
              cores:   !!(c.primaryColor || c.secondaryColor),
              textos:  !!(c.teamName || c.number || c.backText),
              logos:   (c.sponsors || []).length > 0,
            };
            const doneCount = Object.values(done).filter(Boolean).length;
            const totalCount = Object.keys(done).length;
            const pct = Math.round((doneCount / totalCount) * 100);

            if (isMobile) {
              // Mobile: keep the old expand card behavior
              return (
                <PersonalizeCard
                  key={entryId}
                  model={model}
                  qty={cart[modelId]}
                  c={c}
                  onChange={v => setCustom(entryId, v)}
                  onDuplicate={() => duplicate(entryId, modelId)}
                  onDelete={() => deleteEntry(entryId)}
                  isDuplicate={isDuplicate}
                  isOpen={isActive}
                  onToggleOpen={() => setOpenEntryId(id => id === entryId ? null : entryId)}
                />
              );
            }

            // Desktop: clean minimal card
            return (
              <div
                key={entryId}
                onClick={() => setOpenEntryId(isActive ? null : entryId)}
                style={{
                  background: T.surface,
                  border: isActive ? `1.5px solid ${cc}` : `1px solid ${T.border}`,
                  borderRadius: 12, cursor: "pointer", overflow: "hidden",
                  boxShadow: isActive ? `0 2px 16px ${cc}22` : `0 1px 4px ${T.shadow}`,
                  transition: "all .18s", flexShrink: 0,
                  display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                }}
              >
                {/* Left accent */}
                <div style={{ width: 3, alignSelf: "stretch", borderRadius: 3, background: isActive ? cc : T.border, flexShrink: 0, transition: "background .18s" }} />
                {/* Thumb — compact */}
                <div style={{ flexShrink: 0 }}>
                  <ModelThumb model={model} size={52} />
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: isActive ? cc : T.text, marginBottom: 2, display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.customName || model.name}</span>
                    {isDuplicate && <span style={{ fontSize: 9, background: `${cc}18`, color: cc, borderRadius: 4, padding: "1px 5px", fontWeight: 700, flexShrink: 0 }}>cópia</span>}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 5 }}>
                    {(model.categories?.length ? model.categories : [model.category]).map(cat => (
                      <span key={cat} style={{ fontSize: 9, background: `${CAT_COLOR[cat] || cc}15`, color: CAT_COLOR[cat] || cc, borderRadius: 4, padding: "1px 5px", fontWeight: 700, textTransform: "uppercase" }}>{cat}</span>
                    ))}
                  </div>
                  {/* Progress bar */}
                  <div style={{ background: T.border, borderRadius: 3, height: 3 }}>
                    <div style={{ background: pct === 100 ? "#22c55e" : cc, height: 3, borderRadius: 3, width: `${pct}%`, transition: "width .3s" }} />
                  </div>
                  <div style={{ fontSize: 9, color: pct === 100 ? "#22c55e" : T.textFaint, marginTop: 3 }}>
                    {pct === 100 ? "✓ Completo" : `${doneCount}/${totalCount} preenchidos`}
                  </div>
                </div>
                {/* Arrow */}
                <div style={{ color: isActive ? cc : T.border, flexShrink: 0, transition: "color .18s" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
              </div>
            );
          })}
        </div>

        {/* RIGHT — personalization panel (desktop only) */}
        {!isMobile && (
          <div style={{ flex: 1, minWidth: 0, overflowY: "auto", paddingBottom: 8, borderLeft: `1px solid ${T.border}`, paddingLeft: 16 }}>
            {activeModel && activeEntry ? (
              <div>
                {/* Right panel header */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${T.border}` }}>
                  <ModelThumb model={activeModel} size={56} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: T.text }}>{getCustom(activeEntry.entryId).customName || activeModel.name}</div>
                    <div style={{ fontSize: 11, color: T.textFaint, marginTop: 2 }}>{cart[activeEntry.modelId]} un. · R$ {(activeModel.price * cart[activeEntry.modelId]).toFixed(2).replace(".", ",")}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => duplicate(activeEntry.entryId, activeEntry.modelId)} style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 7, color: T.textMuted, padding: "5px 10px", cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                      <Ic.copy /> Duplicar
                    </button>
                    {activeEntry.entryId !== String(activeEntry.modelId) && (
                      <button onClick={() => deleteEntry(activeEntry.entryId)} style={{ background: "#e9456011", border: "1px solid #e9456033", borderRadius: 7, color: "#e94560", padding: "5px 10px", cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                        <Ic.trash2 /> Remover
                      </button>
                    )}
                  </div>
                </div>
                <PersonalizeCard
                  key={activeEntry.entryId}
                  model={activeModel}
                  qty={cart[activeEntry.modelId]}
                  c={getCustom(activeEntry.entryId)}
                  onChange={v => setCustom(activeEntry.entryId, v)}
                  onDuplicate={() => duplicate(activeEntry.entryId, activeEntry.modelId)}
                  onDelete={() => deleteEntry(activeEntry.entryId)}
                  isDuplicate={activeEntry.entryId !== String(activeEntry.modelId)}
                  isOpen={true}
                  onToggleOpen={() => {}}
                  hideHeader={true}
                />
              </div>
            ) : (
              <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, color: T.textFaint }}>
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={T.border3} strokeWidth="1.2"><path d="M20.38 3.46L16 2a4 4 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.57a1 1 0 00.99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.84l.58-3.57a2 2 0 00-1.34-2.23z"/></svg>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.textMuted, marginBottom: 6 }}>Selecione uma modelagem</div>
                  <div style={{ fontSize: 12, color: T.textFaint, maxWidth: 220 }}>Clique em uma modelagem à esquerda para personalizar</div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validation warning (below the panels) */}
      {(() => {
        const missing = entries.filter(({ entryId, modelId }) => {
          const model = MODELS.find(m => m.id === modelId);
          if (!model) return false;
          const hasDesigns = model.path && (window.dataArray || []).some(item => (item.sport_id || "").includes(model.path));
          if (!hasDesigns) return false;
          return !(customizations[entryId] || {}).design;
        });
        return missing.length > 0 ? (
          <div style={{ flexShrink: 0, borderTop: `1px solid ${T.border}`, paddingTop: 10, marginTop: 4 }}>
            <div style={{ background: T.surface2, border: `1.5px solid ${T.border2}`, borderRadius: 11, padding: "10px 14px" }}>
              <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, margin: "0 0 8px" }}>⚠️ Escolha o design antes de continuar:</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {missing.map(({ entryId, modelId }) => {
                  const model = MODELS.find(m => m.id === modelId);
                  const c = customizations[entryId] || {};
                  return (
                    <div key={entryId} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#e9456018", border: "1.5px solid #e9456055", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 }}>🎨</div>
                      <span style={{ fontSize: 12, color: T.textSub }}>{(customizations[entryId] || {}).customName || model?.name} — nenhum design selecionado</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null;
      })()}
    </div>
  );
}

// ─── MINI CALENDAR ───────────────────────────────────────────────────────────
function MiniCalendar({ selected, onSelect }) {
  const T = useT();
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const year = viewDate.getFullYear(); const month = viewDate.getMonth();
  const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const firstDay = new Date(year, month, 1).getDay();
  const daysIn = new Date(year, month + 1, 0).getDate();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const toISO = d => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysIn }, (_, i) => i + 1)];
  return (
    <div style={{ width: "200px", background: T.surface2, border: `1.5px solid ${T.border2}`, borderRadius: 14, padding: "14px 14px 10px", userSelect: "none" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <button onClick={() => setViewDate(new Date(year, month - 1, 1))} style={{ background: T.surface3, border: `1px solid ${T.border}`, borderRadius: 7, color: T.textMuted, padding: "4px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}><Ic.cL /></button>
        <span style={{ color: T.text, fontWeight: 700, fontSize: 14 }}>{MONTHS[month]} {year}</span>
        <button onClick={() => setViewDate(new Date(year, month + 1, 1))} style={{ background: T.surface3, border: `1px solid ${T.border}`, borderRadius: 7, color: T.textMuted, padding: "4px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}><Ic.cR /></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, marginBottom: 4 }}>
        {DAYS.map(d => <div key={d} style={{ textAlign: "center", fontSize: 9, color: T.textFaint, fontWeight: 700, letterSpacing: .5, padding: "2px 0" }}>{d}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const iso = toISO(d); const isPast = iso < todayStr; const isSel = iso === selected; const isToday = iso === todayStr;
          return <button key={i} onClick={() => !isPast && onSelect(iso)} disabled={isPast} style={{ aspectRatio: "1", borderRadius: 8, border: "none", cursor: isPast ? "default" : "pointer", background: isSel ? "#e94560" : isToday ? "#e9456022" : "transparent", color: isPast ? T.textGhost : isSel ? "#fff" : isToday ? "#e94560" : T.textSub, fontWeight: isSel || isToday ? 700 : 400, fontSize: 12, transition: "all .12s", outline: "none" }}>{d}</button>;
        })}
      </div>
      {selected && (
        <div style={{ marginTop: 10, padding: "7px 10px", background: "#e9456011", border: "1px solid #e9456033", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "#e94560", fontSize: 12, fontWeight: 700 }}>{new Date(selected + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}</span>
          <button onClick={() => onSelect(null)} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", display: "flex", alignItems: "center" }}><Ic.x /></button>
        </div>
      )}
    </div>
  );
}


// ─── PDF LOADING OVERLAY ─────────────────────────────────────────────────────
function PdfLoadingOverlay() {
  const T = useT();
  return (
    <div style={{ position: "fixed", inset: 0, background: "#000b", zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 18 }}>
      <div style={{ background: T.surface, border: `1.5px solid ${T.border2}`, borderRadius: 20, padding: "32px 40px", display: "flex", flexDirection: "column", alignItems: "center", gap: 16, boxShadow: `0 24px 60px ${T.shadow}`, minWidth: 240 }}>
        {/* Spinner */}
        <div style={{ width: 48, height: 48, position: "relative" }}>
          <svg width="48" height="48" viewBox="0 0 48 48" style={{ animation: "pdfSpin 1s linear infinite" }}>
            <circle cx="24" cy="24" r="20" fill="none" stroke={T.border3} strokeWidth="4" />
            <path d="M24 4 A20 20 0 0 1 44 24" fill="none" stroke="#22c55e" strokeWidth="4" strokeLinecap="round" />
          </svg>
          <style>{`@keyframes pdfSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: 800, color: T.text, fontSize: 15, marginBottom: 4 }}>Gerando PDF…</div>
          <div style={{ fontSize: 12, color: T.textFaint }}>Carregando imagens e montando o documento</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 18 }}>📄</span>
          <span style={{ fontSize: 11, color: T.textMuted }}>Aguarde, isso pode levar alguns segundos</span>
        </div>
      </div>
    </div>
  );
}

// ─── PDF GENERATOR ───────────────────────────────────────────────────────────
// Helper: load a URL as base64 dataURL (for images in PDF)
async function urlToDataURL(url) {
  try {
    const r = await fetch(url);
    const blob = await r.blob();
    return await new Promise(res => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(blob); });
  } catch { return null; }
}

async function generatePDF(profile, cart, customizations, details, entries, models, storeInfo) {
  if (!window.jspdf) {
    await new Promise((res, rej) => { const s = document.createElement("script"); s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"; s.onload = res; s.onerror = rej; document.head.appendChild(s); });
  }
  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const PW = 210, PH = 297, ML = 18, MR = 18, CW = PW - ML - MR;

  // Store info (dynamic)
  const storeName = storeInfo?.name || "King Sports";
  const storeSite = storeInfo?.site || "";

  const resolvedEntries = (entries && entries.length > 0)
    ? entries.map(e => ({ entryId: e.entryId, model: models.find(m => m.id === e.modelId) })).filter(e => e.model)
    : models.filter(m => cart[m.id] > 0).map(m => ({ entryId: String(m.id), model: m }));
  const total = resolvedEntries.reduce((s, { model }) => s + cart[model.id] * model.price, 0);
  const dateStr = details.deliveryDate ? new Date(details.deliveryDate + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" }) : "—";
  const objStr = (details.objectives || []).map(id => OBJECTIVES.find(o => o.id === id)?.label.replace(/^\S+\s/, "")).join(", ") || "—";

  // Pre-fetch images for each entry (design thumb + sponsor logos)
  const imgCache = {};
  await Promise.all(resolvedEntries.map(async ({ entryId, model: m }) => {
    const c = customizations[entryId] || {};
    // Design thumb
    if (c.design) {
      const allDesigns = (m.designs && m.designs.length > 0) ? m.designs : resolveDesignsForModel(m.path || "");
      const d = allDesigns.find(x => x.id === c.design);
      if (d?.img) { const data = await urlToDataURL(d.img); if (data) imgCache[`design_${entryId}`] = data; }
    }
    // Sponsor logos
    for (const sp of (c.sponsors || [])) {
      if (sp.url && !imgCache[`sp_${sp.id}`]) imgCache[`sp_${sp.id}`] = sp.url; // base64 already
    }
    // Fabric thumb
    if (c.fabric) {
      const f = (m.fabrics || []).find(x => x.id === c.fabric);
      if (f?.img) { const data = await urlToDataURL(f.img); if (data) imgCache[`fabric_${entryId}`] = data; }
    }
    // Shield thumb
    if (c.shield) {
      const s = (m.shields || []).find(x => x.id === c.shield);
      if (s?.img) { const data = await urlToDataURL(s.img); if (data) imgCache[`shield_${entryId}`] = data; }
    }
  }));

  // ── Light theme palette ──────────────────────────────────────────────────────
  const C = {
    bg: "#ffffff",   // page background
    surface: "#f4f6fb",   // card background
    surface2: "#e8ecf4",   // subtle chip bg
    border: "#dde2ee",   // card border
    accent: "#e94560",   // brand red
    accentBg: "#fdf1f3",   // light accent bg
    text: "#1a1a2a",   // main text
    sub: "#3a3a50",   // secondary text
    muted: "#666680",   // muted text
    faint: "#999ab8",   // faint labels
  };

  // Drawing helpers
  const hex2rgb = hex => { const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16); return [isNaN(r) ? 180 : r, isNaN(g) ? 180 : g, isNaN(b) ? 180 : b]; };
  const setFill = hex => doc.setFillColor(...hex2rgb(hex));
  const setStroke = hex => doc.setDrawColor(...hex2rgb(hex));
  const setTxt = hex => doc.setTextColor(...hex2rgb(hex));
  const rect = (x, y, w, h, hex) => { setFill(hex); doc.rect(x, y, w, h, "F"); };
  const roundRect = (x, y, w, h, r, hex, style = "F") => { setFill(hex); doc.roundedRect(x, y, w, h, r, r, style); };
  const line = (x1, y1, x2, y2, hex = C.border, lw = 0.3) => { doc.setLineWidth(lw); setStroke(hex); doc.line(x1, y1, x2, y2); };
  const txt = (text, x, y, opts = {}) => {
    const { size = 10, bold = false, color = C.sub, align = "left", maxW } = opts;
    doc.setFontSize(size); doc.setFont("helvetica", bold ? "bold" : "normal"); setTxt(color);
    if (maxW) doc.text(String(text), x, y, { align, maxWidth: maxW }); else doc.text(String(text), x, y, { align });
  };
  const addImgCover = (dataUrl, x, y, w, h) => {
    try { doc.addImage(dataUrl, "JPEG", x, y, w, h, undefined, "FAST"); } catch {
      try { doc.addImage(dataUrl, "PNG", x, y, w, h, undefined, "FAST"); } catch { }
    }
  };
  const newPage = () => { doc.addPage(); rect(0, 0, PW, PH, C.bg); };

  // ── HEADER ──────────────────────────────────────────────────────────────────
  rect(0, 0, PW, PH, C.bg);
  rect(0, 0, PW, 36, C.bg);
  line(0, 36, PW, 36, C.border, 0.5);
  rect(0, 0, 4, 36, C.accent); // left accent bar
  if (storeInfo?.logoUrl) {
    try {
      const logoData = await urlToDataURL(storeInfo.logoUrl);
      if (logoData) { roundRect(8, 5, 12, 12, 2, C.bg); addImgCover(logoData, 8.5, 5.5, 11, 11); }
      else { setFill(C.accent); doc.circle(14, 12, 2.5, "F"); }
    } catch { setFill(C.accent); doc.circle(14, 12, 2.5, "F"); }
  } else {
    setFill(C.accent); doc.circle(14, 12, 2.5, "F");
  }
  txt(storeName.toUpperCase(), 23, 13, { size: 13, bold: true, color: C.accent });
  txt("Resumo de Pedido", 21, 20, { size: 8, color: C.faint });
  txt(`Gerado em ${new Date().toLocaleDateString("pt-BR")}`, PW - MR, 13, { size: 8, color: C.muted, align: "right" });
  if (storeSite) txt(storeSite, PW - MR, 20, { size: 7, color: C.faint, align: "right" });

  // ── CLIENTE ─────────────────────────────────────────────────────────────────
  let y = 42;
  roundRect(ML, y, CW, 38, 3, C.surface);
  setStroke(C.border); doc.setLineWidth(0.4); doc.roundedRect(ML, y, CW, 38, 3, 3, "S");
  rect(ML, y, 4, 38, C.accent);
  txt((profile.name || "—").toUpperCase(), ML + 10, y + 9, { size: 14, bold: true, color: C.text });
  txt(PLBL[profile.type] || "—", ML + 10, y + 16, { size: 8, color: C.muted });
  [[`Responsável`, details.role || "—"], [`Entrega`, dateStr], [`Objetivo`, objStr]].forEach(([label, value], i) => {
    const cx = ML + i * (CW / 3) + 10;
    txt(label.toUpperCase(), cx, y + 25, { size: 7, color: C.faint });
    txt(value, cx, y + 31, { size: 8, bold: true, color: C.sub, maxW: CW / 3 - 10 });
  });
  y += 44;

  // ── MODELAGENS ───────────────────────────────────────────────────────────────
  txt("MODELAGENS", ML, y, { size: 9, bold: true, color: C.accent });
  line(ML, y + 2, PW - MR, y + 2, C.border, 0.4);
  y += 8;

  for (let idx = 0; idx < resolvedEntries.length; idx++) {
    const { entryId, model: m } = resolvedEntries[idx];
    const c = customizations[entryId] || {};
    const customName = c.customName || m.name;
    const accent = c.primaryColor || "#e94560";

    // Personalisation images available?
    const hasDesignImg = !!imgCache[`design_${entryId}`];
    const hasFabricImg = !!imgCache[`fabric_${entryId}`];
    const hasShieldImg = !!imgCache[`shield_${entryId}`];
    const sponsors = c.sponsors || [];
    const hasSponsor = sponsors.length > 0;
    const imgSize       = 18; // mm — fabric/shield/sponsor thumbs
    const designImgSize = 36; // mm — design thumb (larger for better visibility)
    const imgRow = (hasDesignImg || hasFabricImg || hasShieldImg || hasSponsor);

    // Resolve names
    const allDesigns = (m.designs && m.designs.length > 0) ? m.designs : resolveDesignsForModel(m.path || "");
    const fabricName = c.fabric ? (m.fabrics || []).find(f => f.id === c.fabric)?.name : null;
    const shieldName = c.shield ? (m.shields || []).find(s => s.id === c.shield)?.name : null;
    const designName = c.design ? allDesigns.find(d => d.id === c.design)?.name : null;

    // Font info
    const fonts = [
      c.teamNameFont && `Nome: ${c.teamNameFont}`,
      c.numberFont && `Nº: ${c.numberFont}`,
      c.backTextFont && `Sobrenome: ${c.backTextFont}`,
    ].filter(Boolean);

    // Text chips (name, number, back text)
    const chips = [
      c.teamName && `Nome: ${c.teamName}`,
      c.number && `Nº: ${c.number}`,
      c.backText && `Sobrenome: ${c.backText}`,
    ].filter(Boolean);

    // Model specs
    const specs = [
      m.sizes && `Tam: ${m.sizes}`,
      m.collar && `Gola: ${m.collar}`,
      m.sleeve && `Manga: ${m.sleeve}`,
    ].filter(Boolean);

    // Selected fabric/shield/design names
    const selRow = [
      fabricName && `Tecido: ${fabricName}`,
      shieldName && `Escudo: ${shieldName}`,
      designName && `Design: ${designName}`,
    ].filter(Boolean);

    // Row heights (each line = 6.5mm)
    const LINE = 6.5;
    const QR_SIZE = 22; // mm — reserved space for QR code
    const colorLines = (c.primaryColor || c.secondaryColor ? 1 : 0);
    let rowH = 6              // top pad
      + 9             // title line
      + (specs.length ? LINE : 0)
      + colorLines * LINE
      + chips.length * LINE  // one line per chip
      + (fonts.length ? LINE : 0)
      + (selRow.length ? LINE : 0)
      + (imgRow ? imgSize + 7 : 0)
      + (c.notes ? LINE : 0)
      + QR_SIZE + 4   // QR code space
      + 6;            // bottom pad

    if (y + rowH > PH - 30) { newPage(); y = 16; }

    // Card bg + border + accent bar
    roundRect(ML, y, CW, rowH, 2.5, C.surface);
    setStroke(C.border); doc.setLineWidth(0.3); doc.roundedRect(ML, y, CW, rowH, 2.5, 2.5, "S");
    rect(ML, y, 4, rowH, accent);

    // Index badge
    roundRect(ML + 6, y + 5, 8, 6, 1, C.accentBg);
    txt(String(idx + 1), ML + 10, y + 9.5, { size: 6.5, bold: true, color: C.accent, align: "center" });

    // ── Title line ──────────────────────────────────────────────────────────
    txt(customName, ML + 17, y + 9.5, { size: 11, bold: true, color: C.text, maxW: CW - 52 });
    txt(`×${cart[m.id]}`, PW - MR - 34, y + 9.5, { size: 9, bold: true, color: C.muted });
    txt(`R$ ${(m.price * cart[m.id]).toFixed(2).replace(".", ",")}`, PW - MR - 8, y + 9.5, { size: 11, bold: true, color: C.accent, align: "right" });

    let row = y + 6 + 9; // start after title

    // ── Specs ───────────────────────────────────────────────────────────────
    if (specs.length) {
      txt(specs.join("  ·  "), ML + 17, row, { size: 6.5, color: C.muted, maxW: CW - 22 });
      row += LINE;
    }

    // ── Colours row ─────────────────────────────────────────────────────────
    if (c.primaryColor || c.secondaryColor) {
      let cx = ML + 17;
      const circleR = 2.2;
      if (c.primaryColor) {
        setFill(c.primaryColor); doc.circle(cx + circleR, row + circleR, circleR, "F");
        setStroke("#ffffff"); doc.setLineWidth(0.5); doc.circle(cx + circleR, row + circleR, circleR, "S");
        txt("Cor principal", cx + circleR * 2 + 3, row + 4, { size: 6.5, color: C.faint });
        cx += circleR * 2 + 30;
      }
      if (c.secondaryColor) {
        setFill(c.secondaryColor); doc.circle(cx + circleR, row + circleR, circleR, "F");
        setStroke("#ffffff"); doc.setLineWidth(0.5); doc.circle(cx + circleR, row + circleR, circleR, "S");
        txt("Cor secundária", cx + circleR * 2 + 3, row + 4, { size: 6.5, color: C.faint });
      }
      row += LINE;
    }
    // ── Text chips: one per line ─────────────────────────────────────────────
    chips.forEach(chip => {
      const parts = chip.split(": ");
      const label = parts[0] + ": ";
      const value = parts.slice(1).join(": ");
      // measure label width at the actual font size used
      doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
      const lw = doc.getTextWidth(label) + 1; // +1mm breathing room
      txt(label, ML + 17, row + 4, { size: 6.5, color: C.faint });
      txt(value, ML + 17 + lw, row + 4, { size: 8, bold: true, color: C.text, maxW: CW - 22 - lw });
      row += LINE;
    });

    // ── Fonts used ──────────────────────────────────────────────────────────
    if (fonts.length) {
      txt(`Fontes: ${fonts.join("  ·  ")}`, ML + 17, row, { size: 6, color: C.faint, maxW: CW - 22 });
      row += LINE;
    }

    // ── Fabric / shield / design names ──────────────────────────────────────
    if (selRow.length) {
      txt(selRow.join("   "), ML + 17, row, { size: 6.5, color: C.muted, maxW: CW - 22 });
      row += LINE;
    }

    // ── Images row ──────────────────────────────────────────────────────────
    if (imgRow) {
      const LBL_H = 5;
      let ix = ML + 17;
      const addThumb = (key, label) => {
        if (!imgCache[key]) return;
        roundRect(ix, row, imgSize, imgSize, 1.5, C.bg);
        setStroke(C.border); doc.setLineWidth(0.3); doc.roundedRect(ix, row, imgSize, imgSize, 1.5, 1.5, "S");
        addImgCover(imgCache[key], ix + 0.5, row + 0.5, imgSize - 1, imgSize - 1);
        txt(label, ix + imgSize / 2, row + imgSize + LBL_H - 1, { size: 5.5, color: C.muted, align: "center", maxW: imgSize });
        ix += imgSize + 3;
      };
      if (hasDesignImg) {
        roundRect(ix, row, designImgSize, designImgSize, 2, C.bg);
        setStroke(C.border); doc.setLineWidth(0.3); doc.roundedRect(ix, row, designImgSize, designImgSize, 2, 2, "S");
        addImgCover(imgCache[`design_${entryId}`], ix + 0.5, row + 0.5, designImgSize - 1, designImgSize - 1);
        txt(designName ? designName.slice(0, 16) : "Design", ix + designImgSize / 2, row + designImgSize + LBL_H - 1, { size: 6, color: C.muted, align: "center", maxW: designImgSize });
        ix += designImgSize + 4;
      }
      if (hasFabricImg) addThumb(`fabric_${entryId}`, fabricName ? fabricName.slice(0, 12) : "Tecido");
      if (hasShieldImg) addThumb(`shield_${entryId}`, shieldName ? shieldName.slice(0, 12) : "Escudo");
      sponsors.slice(0, 4).forEach(sp => {
        if (!imgCache[`sp_${sp.id}`]) return;
        roundRect(ix, row, imgSize, imgSize, 1.5, C.bg);
        setStroke(C.border); doc.setLineWidth(0.3); doc.roundedRect(ix, row, imgSize, imgSize, 1.5, 1.5, "S");
        addImgCover(imgCache[`sp_${sp.id}`], ix + 0.5, row + 0.5, imgSize - 1, imgSize - 1);
        const lbl = (sp.name || "Logo").length > 12 ? (sp.name || "Logo").slice(0, 11) + "…" : (sp.name || "Logo");
        txt(lbl, ix + imgSize / 2, row + imgSize + LBL_H - 1, { size: 5.5, color: C.muted, align: "center", maxW: imgSize });
        ix += imgSize + 3;
      });
      row += imgSize + LBL_H + 2;
    }

    // ── Notes ───────────────────────────────────────────────────────────────
    if (c.notes) {
      txt(`Obs: ${c.notes}`, ML + 17, row, { size: 6.5, color: C.muted, maxW: CW - 22 });
      row += LINE;
    }

    // ── QR code placeholder ──────────────────────────────────────────────────
    {
      const qrX = PW - MR - QR_SIZE - 6;
      const qrY = row + 2;
      // dashed border placeholder
      setStroke(C.border); doc.setLineDash([1.5, 1.5]); doc.setLineWidth(0.4);
      doc.roundedRect(qrX, qrY, QR_SIZE, QR_SIZE, 2, 2, "S");
      doc.setLineDash([]);
      // inner icon hint
      txt("QR", qrX + QR_SIZE / 2, qrY + QR_SIZE / 2 - 1, { size: 9, bold: true, color: C.border, align: "center" });
      txt("CODE", qrX + QR_SIZE / 2, qrY + QR_SIZE / 2 + 5, { size: 6, color: C.border, align: "center" });
    }

    y += rowH + 4;
  }

  // ── TOTAL ────────────────────────────────────────────────────────────────────
  if (y + 18 > PH - 30) { newPage(); y = 16; }
  y += 4;
  roundRect(ML, y, CW, 16, 2.5, C.accent);
  txt("TOTAL ESTIMADO", ML + 8, y + 10, { size: 10, bold: true, color: "#ffffff" });
  txt(`R$ ${total.toFixed(2).replace(".", ",")}`, PW - MR - 8, y + 10, { size: 13, bold: true, color: "#ffffff", align: "right" });
  y += 22;

  // ── DETALHES DO PEDIDO ───────────────────────────────────────────────────────
  {
    const detailsRows = [
      details.objectives?.length && ["Objetivos", (details.objectives || []).map(id => OBJECTIVES.find(o => o.id === id)?.label?.replace(/^\S+\s/, "") || id).join(", ")],
      details.deliveryDate && ["Data de Entrega", new Date(details.deliveryDate + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })],
      details.role && ["Papel", details.role],
    ].filter(Boolean);

    if (detailsRows.length || details.description) {
      if (y + 14 > PH - 30) { newPage(); y = 16; }
      txt("DETALHES DO PEDIDO", ML, y, { size: 9, bold: true, color: C.accent });
      line(ML, y + 2, PW - MR, y + 2, C.border, 0.4);
      y += 8;

      // Grid: 3 columns
      detailsRows.forEach(([label, value], i) => {
        const cx = ML + i * (CW / 3);
        if (i === 0 && y + 12 > PH - 20) { newPage(); y = 16; }
        txt(label.toUpperCase(), cx, y, { size: 7, color: C.faint });
        txt(value, cx, y + 5.5, { size: 8, bold: true, color: C.sub, maxW: CW / 3 - 4 });
        if (i === detailsRows.length - 1) y += 14;
      });
    }
  }

  // ── DESCRIÇÃO ────────────────────────────────────────────────────────────────
  if (details.description) {
    if (y + 20 > PH - 30) { newPage(); y = 16; }
    txt("DESCRIÇÃO ADICIONAL", ML, y, { size: 9, bold: true, color: C.accent }); line(ML, y + 2, PW - MR, y + 2, C.border, 0.4); y += 8;
    doc.splitTextToSize(details.description, CW).forEach(l => {
      if (y + 6 > PH - 20) { newPage(); y = 16; }
      txt(l, ML, y, { size: 8.5, color: C.sub }); y += 5.5;
    });
    y += 4;
  }

  // ── ANEXOS ───────────────────────────────────────────────────────────────────
  if ((details.attachments || []).length) {
    if (y + 12 > PH - 20) { newPage(); y = 16; }
    txt("ARQUIVOS ANEXADOS", ML, y, { size: 9, bold: true, color: C.accent }); line(ML, y + 2, PW - MR, y + 2, C.border, 0.4); y += 8;
    (details.attachments || []).forEach(a => {
      if (y + 6 > PH - 20) { newPage(); y = 16; }
      txt(`• ${a.name}`, ML + 4, y, { size: 8, color: C.muted }); y += 5;
    });
  }

  // ── FOOTER ───────────────────────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    const lastY = PH - 12;
    line(ML, lastY - 4, PW - MR, lastY - 4, C.border, 0.3);
    txt(`${storeName}${storeSite ? "  ·  " + storeSite : ""}  ·  Valores para 10–50 unidades.`, ML, lastY, { size: 6.5, color: C.faint });
    txt(`Pág. ${p} de ${totalPages}`, PW - MR, lastY, { size: 6.5, color: C.faint, align: "right" });
  }

  doc.save(`${storeName.toLowerCase().replace(/\s+/g, "-")}-${(profile.name || "pedido").replace(/\s+/g, "-").toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`);
}


// ─── BUILD ORDER JSON ─────────────────────────────────────────────────────────
function buildOrderJSON(profile, cart, customizations, details, entries, models) {
  // models param receives the live context models (passed from App)
  const liveModels = models || [];
  const resolvedEntries = (entries && entries.length > 0)
    ? entries.map(e => ({ entryId: e.entryId, model: liveModels.find(m => m.id === e.modelId) })).filter(e => e.model)
    : liveModels.filter(m => cart[m.id] > 0).map(m => ({ entryId: String(m.id), model: m }));

  const totalEstimado = resolvedEntries.reduce((s, { model }) => s + (cart[model.id] || 0) * model.price, 0);

  const modelagens = resolvedEntries.map(({ entryId, model }) => {
    const c = customizations[entryId] || {};
    const isDuplicate = entryId !== String(model.id);

    const fabricList = model.fabrics || [];
    const shieldList = model.shields || [];
    // designs are lazy — resolve from dataArray
    const designList = (model.designs && model.designs.length > 0)
      ? model.designs
      : resolveDesignsForModel(model.path || "");

    const fabricSel = c.fabric ? fabricList.find(f => f.id === c.fabric) : null;
    const shieldSel = c.shield ? shieldList.find(s => s.id === c.shield) : null;
    const designSel = c.design ? designList.find(d => d.id === c.design) : null;

    // nomePersonalizado from card rename
    const nomePersonalizado = c.customName || null;

    return {
      entryId,
      isDuplicate,
      nomePersonalizado,
      modelagem: {
        id: model.id,
        nome: model.name,
        categoria: model.category,
        path: model.path || null,
        thumb: model.thumb || null,
        preco: model.price,
        tamanhos: model.sizes,
        gola: model.collar,
        manga: model.sleeve,
        extras: model.extras,
        descricao: model.description || null,
      },
      quantidade: cart[model.id] || 0,
      subtotal: parseFloat(((cart[model.id] || 0) * model.price).toFixed(2)),
      personalizacao: {
        corPrincipal: c.primaryColor || null,
        corSecundaria: c.secondaryColor || null,
        nomeDoTime: c.teamName || null,
        fonteNome: c.teamNameFont || null,
        numeracao: c.number || null,
        fonteNumeracao: c.numberFont || null,
        textoCostas: c.backText || null,
        fonteTextoCostas: c.backTextFont || null,
        tecido: fabricSel ? { id: fabricSel.id, nome: fabricSel.name } : null,
        escudo: shieldSel ? { id: shieldSel.id, nome: shieldSel.name } : null,
        design: designSel ? { id: designSel.id, nome: designSel.name, path: designSel.path || null } : null,
        patrocinadores: (c.sponsors || []).map(s => ({ nome: s.name, arquivo: s.url })),
        observacoes: c.notes || null,
      },
    };
  });

  return {
    geradoEm: new Date().toISOString(),
    perfil: {
      tipo: profile.type || null,
      nome: profile.name || null,
      site: profile.site || null,
      instagram: profile.instagram || null,
      facebook: profile.facebook || null,
    },
    pedido: {
      modelagens,
      totalEstimado: parseFloat(totalEstimado.toFixed(2)),
    },
    detalhes: {
      objetivos: (details.objectives || []).map(id => {
        const o = OBJECTIVES.find(x => x.id === id);
        return o ? { id: o.id, label: o.label } : { id };
      }),
      dataEntrega: details.deliveryDate || null,
      papel: details.role || null,
      descricao: details.description || null,
      anexos: (details.attachments || []).map(a => ({
        nome: a.name,
        tipo: a.type,
        tamanho: a.size,
        // base64 disponível em a.url se necessário
      })),
    },
  };
}

// ─── STEP 4 ──────────────────────────────────────────────────────────────────
function Step4({ details, setDetails, cart, customizations, entries, profile, onBack, cartBtn }) {
  const T = useT();
  const storeInfo = useStore();
  const [pdfLoading, setPdfLoading] = useState(false);
  // dentro do Step4
  const MODELS = useModels()
  const resolvedEntries = (entries && entries.length > 0)
    ? entries.map(e => ({ entryId: e.entryId, model: MODELS.find(m => m.id === e.modelId) })).filter(e => e.model)
    : MODELS.filter(m => cart[m.id] > 0).map(m => ({ entryId: String(m.id), model: m }));
  const total = resolvedEntries.reduce((s, { model }) => s + cart[model.id] * model.price, 0);
  const [submitted, setSubmitted] = useState(false);
  const [sim3dModel, setSim3dModel] = useState(null);
  const [threeLoaded, setThreeLoaded] = useState(!!window.THREE);
  const attachRef = useRef();
  useEffect(() => { if (window.THREE) { setThreeLoaded(true); return; } const s = document.createElement("script"); s.src = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js"; s.onload = () => setThreeLoaded(true); document.head.appendChild(s); }, []);
  const set = (k, v) => setDetails(d => ({ ...d, [k]: v }));
  const toggleObj = id => set("objectives", (details.objectives || []).includes(id) ? (details.objectives || []).filter(x => x !== id) : [...(details.objectives || []), id]);
  const handleAttach = e => { Array.from(e.target.files || []).forEach(f => { const r = new FileReader(); r.onload = ev => set("attachments", [...(details.attachments || []), { id: nid(), name: f.name, size: f.size, url: ev.target.result, type: f.type }]); r.readAsDataURL(f); }); e.target.value = ""; };
  const fmtSize = b => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)}MB` : `${(b / 1024).toFixed(0)}KB`;
  const canSubmit = (details.objectives || []).length > 0 && details.deliveryDate && details.role;

  // List of what's still missing — used for the inline hint
  const pendingItems = [
    (details.objectives || []).length === 0 && { key: "obj", icon: "🎯", label: "Selecione pelo menos um objetivo" },
    !details.deliveryDate && { key: "date", icon: "📅", label: "Escolha a data de entrega" },
    !details.role && { key: "role", icon: "👤", label: "Informe seu papel na organização" },
  ].filter(Boolean);

  if (submitted) return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ textAlign: "center", padding: "24px 0 10px", flexShrink: 0 }}>
        <div style={{ fontSize: 44, marginBottom: 10 }}>🎉</div>
        <h2 style={{ color: T.text, fontWeight: 800, fontSize: 20, marginBottom: 4 }}>Orçamento Enviado!</h2>
        <p style={{ color: T.textFaint, fontSize: 12, marginBottom: 20 }}>Nossa equipe entrará em contato em breve.</p>
      </div>
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0, marginBottom: 8 }}>
        <div style={{ background: T.surface2, border: `1.5px solid ${T.border2}`, borderRadius: 13, padding: "14px 16px", marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
            <span style={{ color: T.textSub, fontWeight: 700 }}>{profile.name}</span>{details.role && <span style={{ color: "#3b82f6" }}> · {details.role}</span>}
          </div>
          {resolvedEntries.map(({ entryId, model: m }) => (<div key={entryId} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 5 }}><span style={{ color: T.textMuted }}>{m.name}{entryId !== String(m.id) ? " (cópia)" : ""} <span style={{ color: T.textFaint }}>×{cart[m.id]}</span></span><span style={{ color: T.textSub }}>R$ {(m.price * cart[m.id]).toFixed(2).replace(".", ",")}</span></div>))}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 14, borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 6 }}>
            <span style={{ color: T.text }}>Total</span><span style={{ color: "#e94560" }}>R$ {total.toFixed(2).replace(".", ",")}</span>
          </div>
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, flexShrink: 0 }}>
        <button onClick={async () => { setPdfLoading(true); try { await generatePDF(profile, cart, customizations, details, entries, MODELS, storeInfo); } finally { setPdfLoading(false); } }} style={{ ...sBtnG(T), justifyContent: "center", border: "1.5px solid #22c55e44", color: "#22c55e", gap: 6 }}>📄 Baixar PDF</button>
        <button onClick={() => { const orderJSON = buildOrderJSON(profile, cart, customizations, details, entries, MODELS); window.__kingOrderJSON = orderJSON; window.dispatchEvent(new CustomEvent("kingsports:order", { detail: orderJSON })); if (typeof window["receiveDataFromForm"] === "function") window["receiveDataFromForm"](orderJSON); }} style={{ ...sBtnG(T), justifyContent: "center", border: "1.5px solid #a78bfa44", color: "#a78bfa", gap: 6 }}>👕 Simulador 3D</button>
      </div>
      {pdfLoading && <PdfLoadingOverlay />}
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ flexShrink: 0, marginBottom: 12, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 2 }}>Detalhes do Pedido</h2>
          <p style={{ color: T.textFaint, fontSize: 12, margin: 0 }}>Últimas informações para enviarmos o orçamento ideal</p>
        </div>
        {cartBtn}
      </div>
      {/* Scrollable content */}
      <div style={{
        flex: 1, overflowY: "auto", minHeight: 0, display: "flex", flexDirection: "column", gap: 16, maxHeight: isMobile
          ? "calc(100vh - 380px)"
          : "calc(100vh - 320px)"
      }}>
        {/* Objectives */}
        <div>
          <label style={{ ...sLbl(T), marginBottom: 8, fontSize: 11, color: T.textMuted }}>🎯 Objetivo do pedido <span style={{ color: "#e94560" }}>*</span></label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
            {OBJECTIVES.map(obj => {
              const sel = (details.objectives || []).includes(obj.id); return (
                <button key={obj.id} onClick={() => toggleObj(obj.id)} style={{ background: sel ? "#e9456011" : T.surface2, border: sel ? "1.5px solid #e94560" : `1.5px solid ${T.border2}`, borderRadius: 50, padding: "7px 14px", cursor: "pointer", color: sel ? "#e94560" : T.textMuted, fontSize: 12, fontWeight: sel ? 700 : 400, transition: "all .15s", display: "flex", alignItems: "center", gap: 5 }}>
                  {sel && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#e94560", flexShrink: 0 }} />}
                  {obj.label}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {/* Calendar */}
          <div>
            <label style={{ ...sLbl(T), marginBottom: 8, fontSize: 11, color: T.textMuted, display: "flex", alignItems: "center", gap: 5 }}><Ic.cal /> Data de entrega <span style={{ color: "#e94560" }}>*</span></label>
            <MiniCalendar selected={details.deliveryDate || null} onSelect={v => set("deliveryDate", v)} />
          </div>
          {/* Role */}
          <div>
            <label style={{ ...sLbl(T), marginBottom: 8, fontSize: 11, color: T.textMuted, display: "flex", alignItems: "center", gap: 5 }}><Ic.user /> Papel na organização <span style={{ color: "#e94560" }}>*</span></label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {ROLES.map(role => {
                const sel = details.role === role; return (
                  <button key={role} onClick={() => set("role", role)} style={{ background: sel ? "#3b82f611" : T.surface2, border: sel ? "1.5px solid #3b82f6" : `1.5px solid ${T.border2}`, borderRadius: 8, padding: "6px 13px", cursor: "pointer", color: sel ? "#60a5fa" : T.textMuted, fontSize: 11, fontWeight: sel ? 700 : 400, transition: "all .14s", display: "flex", alignItems: "center", gap: 5 }}>
                    {sel && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#3b82f6" }} />}
                    {role}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <label style={{ ...sLbl(T), marginBottom: 6, fontSize: 11, color: T.textMuted }}>📝 Descrição adicional</label>
          <textarea value={details.description || ""} onChange={e => set("description", e.target.value)} placeholder="Detalhes extras, referências, prazo urgente..." style={{ ...sInp(T), minHeight: 72, resize: "vertical" }} />
        </div>
        {/* Attachments */}
        <div>
          <label style={{ ...sLbl(T), marginBottom: 8, fontSize: 11, color: T.textMuted }}>📎 Anexar documentos / referências</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            {(details.attachments || []).map(a => {
              const isImg = a.type?.startsWith("image/"); return (
                <div key={a.id} style={{ background: T.surface2, border: `1px solid ${T.border2}`, borderRadius: 9, padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, maxWidth: 220 }}>
                  {isImg ? <img src={a.url} alt={a.name} style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 5, flexShrink: 0 }} /> : <div style={{ width: 32, height: 32, background: T.surface3, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>📄</div>}
                  <div style={{ minWidth: 0, flex: 1 }}><div style={{ fontSize: 11, color: T.textSub, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</div><div style={{ fontSize: 9, color: T.textFaint }}>{fmtSize(a.size)}</div></div>
                  <button onClick={() => set("attachments", (details.attachments || []).filter(x => x.id !== a.id))} style={{ background: "none", border: "none", color: T.textFaint, cursor: "pointer", display: "flex", padding: 2 }}><Ic.x /></button>
                </div>
              );
            })}
            <button onClick={() => attachRef.current?.click()} style={{ background: T.surface2, border: `1.5px dashed ${T.border3}`, borderRadius: 9, padding: "10px 16px", color: T.textFaint, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
              <Ic.img /> Adicionar arquivo
            </button>
          </div>
          <input ref={attachRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" style={{ display: "none" }} onChange={handleAttach} />
          <p style={{ fontSize: 10, color: T.textGhost, margin: 0 }}>Imagens, PDFs, documentos Word</p>
        </div>
        {/* Summary */}
        <div style={{ background: T.surface2, border: `1.5px solid #e9456033`, borderRadius: 13, padding: 14 }}>
          <h3 style={{ fontWeight: 800, fontSize: 13, color: T.text, marginBottom: 9 }}>Resumo Final</h3>
          <div style={{ fontSize: 11, color: T.textFaint, marginBottom: 8, paddingBottom: 7, borderBottom: `1px solid ${T.border}` }}>
            {PLBL[profile.type]} · <span style={{ color: T.textSub }}>{profile.name}</span>{details.role && <span style={{ color: "#3b82f6", marginLeft: 6 }}>· {details.role}</span>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {resolvedEntries.map(({ entryId, model: m }) => (<div key={entryId} style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}><span style={{ color: T.textMuted }}>{m.name}{entryId !== String(m.id) ? " (cópia)" : ""} <span style={{ color: T.textFaint }}>×{cart[m.id]}</span></span><span style={{ color: T.textSub }}>R$ {(m.price * cart[m.id]).toFixed(2).replace(".", ",")}</span></div>))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 800, borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 7 }}>
            <span style={{ color: T.text }}>Total Estimado</span><span style={{ color: "#e94560" }}>R$ {total.toFixed(2).replace(".", ",")}</span>
          </div>
          {details.deliveryDate && <p style={{ fontSize: 10, color: T.textMuted, margin: "5px 0 0" }}>📅 {new Date(details.deliveryDate + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}</p>}
          {(details.objectives || []).length > 0 && <p style={{ fontSize: 10, color: T.textMuted, margin: "3px 0 0" }}>🎯 {(details.objectives || []).map(id => OBJECTIVES.find(o => o.id === id)?.label).join(", ")}</p>}
        </div>
        {/* Validation chips — shown when submit is blocked */}
        {!canSubmit && (
          <div style={{ marginTop: 2, background: T.surface2, border: `1.5px solid ${T.border2}`, borderRadius: 11, padding: "10px 14px", flexShrink: 0, marginBottom: 4 }}>
            <p style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, margin: "0 0 8px" }}>
              ⚠️ Para solicitar o orçamento, preencha:
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {pendingItems.map(item => (
                <div key={item.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#e9456018", border: "1.5px solid #e9456055", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 }}>{item.icon}</div>
                  <span style={{ fontSize: 12, color: T.textSub }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pinned nav */}
      <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 10, paddingTop: 12, display: "flex", justifyContent: "flex-start", flexShrink: 0, gap: 8 }}>
        <button onClick={onBack} style={sBtnG(T)}><Ic.cL /> Voltar</button>
        <button
          onClick={async () => { if (!canSubmit) return; setPdfLoading(true); try { await generatePDF(profile, cart, customizations, details, entries, MODELS, storeInfo); } finally { setPdfLoading(false); } }}
          disabled={!canSubmit}
          style={{ background: canSubmit ? "#22c55e18" : T.surface3, border: canSubmit ? "1.5px solid #22c55e55" : `1.5px solid ${T.border2}`, borderRadius: 10, color: canSubmit ? "#22c55e" : T.textGhost, padding: "10px 8px", cursor: canSubmit ? "pointer" : "not-allowed", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, opacity: canSubmit ? 1 : 0.5 }}
        >
          <span style={{ fontSize: 20 }}>📄</span>Baixar PDF
        </button>
        <button
          onClick={() => {
            if (!canSubmit) return;
            const orderJSON = buildOrderJSON(profile, cart, customizations, details, entries, MODELS);
            window.__kingOrderJSON = orderJSON;
            window.dispatchEvent(new CustomEvent("kingsports:order", { detail: orderJSON }));
            if (typeof window["receiveDataFromForm"] === "function") window["receiveDataFromForm"](orderJSON);
          }}
          disabled={!canSubmit}
          style={{ background: canSubmit ? "#a78bfa18" : T.surface3, border: canSubmit ? "1.5px solid #a78bfa55" : `1.5px solid ${T.border2}`, borderRadius: 10, color: canSubmit ? "#a78bfa" : T.textGhost, padding: "10px 8px", cursor: canSubmit ? "pointer" : "not-allowed", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, opacity: canSubmit ? 1 : 0.5 }}
        >
          <span style={{ fontSize: 20 }}>👕</span>Simulador 3D
        </button>
        <button
          onClick={() => {
            if (!canSubmit) return;
            const orderJSON = buildOrderJSON(profile, cart, customizations, details, entries, MODELS);
            window.__kingOrderJSON = orderJSON;
            window.dispatchEvent(new CustomEvent("kingsports:order", { detail: orderJSON }));
            setSubmitted(true);
          }}
          disabled={!canSubmit}
          style={{ background: canSubmit ? "linear-gradient(135deg,#e94560,#b5213a)" : T.surface3, border: canSubmit ? "none" : `1.5px solid ${T.border2}`, borderRadius: 10, color: canSubmit ? "#fff" : T.textGhost, padding: "10px 8px", cursor: canSubmit ? "pointer" : "not-allowed", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, opacity: canSubmit ? 1 : 0.6 }}
        >
          <span style={{ fontSize: 20 }}>🚀</span>Solicitar Orçamento
        </button>
      </div>

      {/* PDF loading overlay — rendered over everything */}
      {pdfLoading && <PdfLoadingOverlay />}
    </div>
  );
}


// ─── CART PANEL (sidebar on desktop, drawer on mobile) ───────────────────────
function CartPanel({ cart, entries, customizations, onClose, isMobile }) {
  const T = useT();

  // Build display list from entries (respects duplicates) or fall back to cart
  const items = entries && entries.length > 0
    ? entries.map(e => ({ entryId: e.entryId, model: MODELS.find(m => m.id === e.modelId) })).filter(e => e.model)
    : MODELS.filter(m => cart[m.id] > 0).map(m => ({ entryId: String(m.id), model: m }));

  const total = items.reduce((s, { model }) => s + cart[model.id] * model.price, 0);
  const totalQty = items.reduce((s, { model }) => s + (cart[model.id] || 0), 0);

  const panelStyle = isMobile ? {
    position: "fixed", inset: 0, zIndex: 950,
    display: "flex", flexDirection: "column",
  } : {
    width: 240, flexShrink: 0,
    display: "flex", flexDirection: "column",
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: 14,
    overflow: "hidden",
  };

  const inner = (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ padding: "12px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: T.surface, flexShrink: 0 }}>
        <div>
          <div style={{ fontWeight: 800, color: T.text, fontSize: 13, display: "flex", alignItems: "center", gap: 7 }}>
            <Ic.cart /> Carrinho
            <span style={{ background: "#e94560", color: "#fff", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>{totalQty}</span>
          </div>
          <div style={{ fontSize: 10, color: T.textFaint, marginTop: 2 }}>{items.length} {items.length === 1 ? "modelagem" : "modelagens"}</div>
        </div>
        {isMobile && (
          <button onClick={onClose} style={{ background: T.surface2, border: "none", borderRadius: 8, color: T.textMuted, padding: "5px 7px", cursor: "pointer", display: "flex" }}><Ic.x /></button>
        )}
      </div>

      {/* Items list */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px", display: "flex", flexDirection: "column", gap: 7, background: T.surface }}>
        {items.length === 0 && (
          <div style={{ textAlign: "center", padding: "30px 0", color: T.textGhost, fontSize: 12 }}>🛒<br />Nenhum item</div>
        )}
        {items.map(({ entryId, model }) => {
          const cc = CAT_COLOR[model.category] || "#888";
          const qty = cart[model.id] || 0;
          const c = customizations[entryId] || {};
          const isDup = entryId !== String(model.id);
          return (
            <div key={entryId} style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10, padding: "8px 10px", display: "flex", gap: 9, alignItems: "flex-start" }}>
              <img src={`./form/${model.id}.webp`} alt={model.name} style={{ width: 38, height: 38, objectFit: "contain", borderRadius: 6, flexShrink: 0, background: T.surface3 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{model.name}</span>
                  {isDup && <span style={{ fontSize: 9, color: cc, background: `${cc}22`, borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>cópia</span>}
                </div>
                <div style={{ fontSize: 10, color: T.textFaint, marginTop: 1 }}>{model.category} · ×{qty}</div>
                {/* Mini personalisation summary */}
                {(c.primaryColor || c.teamName || c.design) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                    {c.primaryColor && <div style={{ width: 10, height: 10, borderRadius: "50%", background: c.primaryColor, border: `1px solid ${T.border3}`, flexShrink: 0 }} title="Cor principal" />}
                    {c.secondaryColor && <div style={{ width: 10, height: 10, borderRadius: "50%", background: c.secondaryColor, border: `1px solid ${T.border3}`, flexShrink: 0 }} title="Cor secundária" />}
                    {c.teamName && <span style={{ fontSize: 9, color: T.textMuted, fontFamily: c.teamNameFont ? `'${c.teamNameFont}',sans-serif` : "inherit", fontWeight: 700, maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.teamName}</span>}
                    {c.design && <span style={{ fontSize: 9, color: cc, background: `${cc}18`, borderRadius: 4, padding: "1px 5px", fontWeight: 600 }}>🎨</span>}
                  </div>
                )}
                <div style={{ fontSize: 11, color: cc, fontWeight: 700, marginTop: 3 }}>R$ {(model.price * qty).toFixed(2).replace(".", ",")}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total footer */}
      <div style={{ padding: "10px 14px", borderTop: `1px solid ${T.border}`, background: T.surface, flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 600 }}>Total estimado</span>
          <span style={{ fontSize: 15, color: "#e94560", fontWeight: 900 }}>R$ {total.toFixed(2).replace(".", ",")}</span>
        </div>
        <div style={{ fontSize: 9, color: T.textGhost, marginTop: 3 }}>* 10–50 un. Escudo bordado +valor</div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <div style={{ position: "fixed", inset: 0, background: "#000a", zIndex: 940 }} onClick={onClose} />
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 950, background: T.surface, borderRadius: "18px 18px 0 0", border: `1.5px solid ${T.border2}`, borderBottom: "none", maxHeight: "75vh", display: "flex", flexDirection: "column", boxShadow: `0 -12px 40px ${T.shadow}` }}>
          <div style={{ padding: "10px 0 6px", display: "flex", justifyContent: "center", flexShrink: 0 }}>
            <div style={{ width: 36, height: 4, borderRadius: 4, background: T.border3 }} />
          </div>
          {inner.props.children}
        </div>
      </>
    );
  }

  return inner;
}



// ─── CART FLOAT BUTTON ────────────────────────────────────────────────────────
function CartFloatBtn({ qty, onClick, T }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: qty > 0 ? "#e9456011" : T.surface2,
        border: qty > 0 ? "1.5px solid #e9456055" : `1.5px solid ${T.border2}`,
        borderRadius: 10, padding: "6px 12px", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 6, transition: "all .2s",
        flexShrink: 0, whiteSpace: "nowrap",
      }}
    >
      <Ic.cart />
      <span style={{ color: T.textSub, fontSize: 12, fontWeight: 600 }}>Carrinho</span>
      {qty > 0 && (
        <span style={{ background: "#e94560", color: "#fff", borderRadius: 50, minWidth: 18, height: 18, padding: "0 4px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>{qty}</span>
      )}
    </button>
  );
}

// ─── CART SIDEBAR ─────────────────────────────────────────────────────────────
function CartSidebar({ cart, entries, customizations }) {
  const T = useT();
  const items = entries && entries.length > 0
    ? entries.map(e => ({ entryId: e.entryId, model: MODELS.find(m => m.id === e.modelId) })).filter(e => e.model)
    : MODELS.filter(m => cart[m.id] > 0).map(m => ({ entryId: String(m.id), model: m }));
  const total = items.reduce((s, { model }) => s + (cart[model.id] || 0) * model.price, 0);
  const totalQty = items.reduce((s, { model }) => s + (cart[model.id] || 0), 0);

  return (
    <div style={{ width: 220, flexShrink: 0, display: "flex", flexDirection: "column", background: T.surface, border: `1.5px solid ${T.border}`, borderRadius: 14, overflow: "hidden", height: "100%" }}>
      {/* Header */}
      <div style={{ padding: "12px 14px 10px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Ic.cart />
          <span style={{ fontWeight: 800, color: T.text, fontSize: 13 }}>Carrinho</span>
          <span style={{ background: "#e94560", color: "#fff", borderRadius: 50, minWidth: 18, height: 18, padding: "0 5px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, marginLeft: "auto" }}>{totalQty}</span>
        </div>
        <div style={{ fontSize: 10, color: T.textFaint, marginTop: 3 }}>{items.length} {items.length === 1 ? "modelagem" : "modelagens"}</div>
      </div>

      {/* Items */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px", display: "flex", flexDirection: "column", gap: 8 }}>
        {items.length === 0 && (
          <div style={{ textAlign: "center", padding: "30px 0", color: T.textGhost, fontSize: 12 }}>Nenhum item</div>
        )}
        {items.map(({ entryId, model }) => {
          const cc = CAT_COLOR[model.category] || "#888";
          const qty = cart[model.id] || 0;
          const c = customizations[entryId] || {};
          const isDup = entryId !== String(model.id);

          // resolve names
          const fabricList = model.fabrics || [];
          const shieldList = model.shields || [];
          const designList = model.designs || [];
          const fabricName = c.fabric ? fabricList.find(f => f.id === c.fabric)?.name : null;
          const shieldName = c.shield ? shieldList.find(s => s.id === c.shield)?.name : null;
          const designName = c.design ? designList.find(d => d.id === c.design)?.name : null;

          return (
            <div key={entryId} style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
              {/* Color accent bar */}
              <div style={{ height: 3, background: c.primaryColor || cc }} />
              <div style={{ padding: "8px 10px", display: "flex", gap: 8, alignItems: "flex-start" }}>
                <img src={`./form/${model.id}.webp`} alt={model.name} style={{ width: 36, height: 36, objectFit: "contain", borderRadius: 6, flexShrink: 0, background: T.surface3 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.text, lineHeight: 1.2 }}>{model.name}</span>
                    {isDup && <span style={{ fontSize: 8, color: cc, background: `${cc}22`, borderRadius: 3, padding: "1px 4px", fontWeight: 700 }}>cópia</span>}
                  </div>
                  <div style={{ fontSize: 10, color: T.textFaint, marginTop: 1 }}>×{qty} · R$ {(model.price * qty).toFixed(2).replace(".", ",")}</div>

                  {/* Personalisation chips */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 5 }}>
                    {c.primaryColor && <div style={{ width: 10, height: 10, borderRadius: "50%", background: c.primaryColor, border: `1px solid ${T.border3}`, flexShrink: 0 }} title="Cor principal" />}
                    {c.secondaryColor && <div style={{ width: 10, height: 10, borderRadius: "50%", background: c.secondaryColor, border: `1px solid ${T.border3}`, flexShrink: 0 }} title="Cor secundária" />}
                    {c.teamName && <span style={{ fontSize: 9, color: T.textSub, fontWeight: 700, maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: c.teamNameFont ? `'${c.teamNameFont}',sans-serif` : "inherit" }}>{c.teamName}</span>}
                    {fabricName && <span style={{ fontSize: 8, background: T.surface3, border: `1px solid ${T.border}`, borderRadius: 3, padding: "1px 4px", color: T.textMuted, whiteSpace: "nowrap" }}>🧵 {fabricName}</span>}
                    {shieldName && <span style={{ fontSize: 8, background: T.surface3, border: `1px solid ${T.border}`, borderRadius: 3, padding: "1px 4px", color: T.textMuted, whiteSpace: "nowrap" }}>🛡️ {shieldName}</span>}
                    {designName && <span style={{ fontSize: 8, background: `${cc}18`, border: `1px solid ${cc}33`, borderRadius: 3, padding: "1px 4px", color: cc, whiteSpace: "nowrap" }}>🎨 {designName}</span>}
                    {(c.sponsors || []).length > 0 && <span style={{ fontSize: 8, background: T.surface3, border: `1px solid ${T.border}`, borderRadius: 3, padding: "1px 4px", color: T.textMuted }}>📎 {c.sponsors.length} logo{c.sponsors.length > 1 ? "s" : ""}</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total */}
      <div style={{ padding: "10px 14px", borderTop: `1px solid ${T.border}`, flexShrink: 0, background: T.surface }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontSize: 11, color: T.textMuted }}>Total estimado</span>
          <span style={{ fontSize: 16, color: "#e94560", fontWeight: 900 }}>R$ {total.toFixed(2).replace(".", ",")}</span>
        </div>
        <div style={{ fontSize: 9, color: T.textGhost, marginTop: 2 }}>* 10–50 un. Escudo bordado +valor</div>
      </div>
    </div>
  );
}

// ─── CART DRAWER ─────────────────────────────────────────────────────────────
function CartDrawer({ cart, entries, customizations, onClose }) {
  const T = useT();

  const items = entries && entries.length > 0
    ? entries.map(e => ({ entryId: e.entryId, model: MODELS.find(m => m.id === e.modelId) })).filter(e => e.model)
    : MODELS.filter(m => cart[m.id] > 0).map(m => ({ entryId: String(m.id), model: m }));
  const total = items.reduce((s, { model }) => s + (cart[model.id] || 0) * model.price, 0);
  const totalQty = items.reduce((s, { model }) => s + (cart[model.id] || 0), 0);

  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "#000a", zIndex: 940 }} onClick={onClose} />
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 950,
        width: "min(340px, 92vw)",
        background: T.surface,
        borderLeft: `1.5px solid ${T.border2}`,
        display: "flex", flexDirection: "column",
        boxShadow: `-12px 0 40px ${T.shadow}`,
      }}>
        {/* Header */}
        <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Ic.cart />
            <span style={{ fontWeight: 800, color: T.text, fontSize: 15 }}>Carrinho</span>
            <span style={{ background: "#e94560", color: "#fff", borderRadius: 50, minWidth: 20, height: 20, padding: "0 5px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800 }}>{totalQty}</span>
          </div>
          <button onClick={onClose} style={{ background: T.surface2, border: "none", borderRadius: 8, color: T.textMuted, padding: "5px 8px", cursor: "pointer", display: "flex" }}><Ic.x /></button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
          {items.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 0", color: T.textGhost, fontSize: 13 }}>🛒<br />Nenhum item</div>
          )}
          {items.map(({ entryId, model }) => {
            const cc = CAT_COLOR[model.category] || "#888";
            const qty = cart[model.id] || 0;
            const c = customizations[entryId] || {};
            const isDup = entryId !== String(model.id);
            const fabricList = model.fabrics || [];
            const shieldList = model.shields || [];
            const designList = model.designs || [];
            const fabricName = c.fabric ? fabricList.find(f => f.id === c.fabric)?.name : null;
            const shieldName = c.shield ? shieldList.find(s => s.id === c.shield)?.name : null;
            const designName = c.design ? designList.find(d => d.id === c.design)?.name : null;
            return (
              <div key={entryId} style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                <div style={{ height: 3, background: c.primaryColor || cc }} />
                <div style={{ padding: "9px 11px", display: "flex", gap: 9, alignItems: "flex-start" }}>
                  <img src={`./form/${model.id}.webp`} alt={model.name} style={{ width: 40, height: 40, objectFit: "contain", borderRadius: 7, flexShrink: 0, background: T.surface3 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{model.name}</span>
                      {isDup && <span style={{ fontSize: 9, color: cc, background: `${cc}22`, borderRadius: 3, padding: "1px 4px", fontWeight: 700 }}>cópia</span>}
                    </div>
                    <div style={{ fontSize: 10, color: T.textFaint, marginTop: 1 }}>{model.category} · ×{qty}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 5 }}>
                      {c.primaryColor && <div style={{ width: 10, height: 10, borderRadius: "50%", background: c.primaryColor, border: `1px solid ${T.border3}`, flexShrink: 0 }} />}
                      {c.secondaryColor && <div style={{ width: 10, height: 10, borderRadius: "50%", background: c.secondaryColor, border: `1px solid ${T.border3}`, flexShrink: 0 }} />}
                      {c.teamName && <span style={{ fontSize: 9, color: T.textSub, fontWeight: 700, maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: c.teamNameFont ? `'${c.teamNameFont}',sans-serif` : "inherit" }}>{c.teamName}</span>}
                      {fabricName && <span style={{ fontSize: 8, background: T.surface3, border: `1px solid ${T.border}`, borderRadius: 3, padding: "1px 4px", color: T.textMuted, whiteSpace: "nowrap" }}>🧵 {fabricName}</span>}
                      {shieldName && <span style={{ fontSize: 8, background: T.surface3, border: `1px solid ${T.border}`, borderRadius: 3, padding: "1px 4px", color: T.textMuted, whiteSpace: "nowrap" }}>🛡️ {shieldName}</span>}
                      {designName && <span style={{ fontSize: 8, background: `${cc}18`, border: `1px solid ${cc}33`, borderRadius: 3, padding: "1px 4px", color: cc, whiteSpace: "nowrap" }}>🎨 {designName}</span>}
                      {(c.sponsors || []).length > 0 && <span style={{ fontSize: 8, background: T.surface3, border: `1px solid ${T.border}`, borderRadius: 3, padding: "1px 4px", color: T.textMuted }}>📎 {c.sponsors.length} logo{c.sponsors.length > 1 ? "s" : ""}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: cc, fontWeight: 700, marginTop: 4 }}>R$ {(model.price * qty).toFixed(2).replace(".", ",")}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Total footer */}
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 600 }}>Total estimado</span>
            <span style={{ fontSize: 17, color: "#e94560", fontWeight: 900 }}>R$ {total.toFixed(2).replace(".", ",")}</span>
          </div>
          <div style={{ fontSize: 9, color: T.textGhost, marginTop: 3 }}>* 10–50 un. Escudo bordado +valor</div>
        </div>
      </div>
    </>
  );
}

// ─── STORE BOTTOM SHEET ──────────────────────────────────────────────────────
function StoreBottomSheet({ onClose }) {
  const T = useT();
  const S = useStore(); // 👈 pega os dados da loja
  const [dragY, setDragY] = useState(0); const [dragging, setDragging] = useState(false); const startY = useRef(0);
  const onDragStart = e => { setDragging(true); startY.current = e.clientY ?? e.touches?.[0]?.clientY; };
  const onDragMove = e => { if (!dragging) return; const y = (e.clientY ?? e.touches?.[0]?.clientY) - startY.current; if (y > 0) setDragY(y); };
  const onDragEnd = () => { setDragging(false); if (dragY > 80) onClose(); else setDragY(0); };
  const InfoRow = ({ icon, label, value, href }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
      <div style={{ width: 32, height: 32, borderRadius: 9, background: "#e9456011", border: "1px solid #e9456033", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 15 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, color: T.textFaint, textTransform: "uppercase", letterSpacing: .8, marginBottom: 2 }}>{label}</div>
        {href ? <a href={href} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#e94560", fontWeight: 600, textDecoration: "none", wordBreak: "break-all" }}>{value}</a> : <div style={{ fontSize: 13, color: T.textSub, fontWeight: 500, wordBreak: "break-word" }}>{value}</div>}
      </div>
    </div>
  );
  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "#000a", zIndex: 1200 }} onClick={onClose} />
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1201, background: T.surface, borderRadius: "20px 20px 0 0", border: `1.5px solid ${T.border2}`, borderBottom: "none", boxShadow: `0 -20px 60px ${T.shadow}`, transform: `translateY(${dragY}px)`, transition: dragging ? "none" : "transform .35s cubic-bezier(.32,.72,0,1)", maxHeight: "88vh", display: "flex", flexDirection: "column", userSelect: "none" }}
        onMouseMove={onDragMove} onMouseUp={onDragEnd} onMouseLeave={onDragEnd} onTouchMove={onDragMove} onTouchEnd={onDragEnd}>
        <div style={{ padding: "12px 0 8px", display: "flex", justifyContent: "center", cursor: "grab", flexShrink: 0 }} onMouseDown={onDragStart} onTouchStart={onDragStart}>
          <div style={{ width: 40, height: 4, borderRadius: 4, background: T.border3 }} />
        </div>
        <div style={{ overflowY: "auto", flex: 1, padding: "0 20px 32px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: S.logoUrl ? T.surface2 : "linear-gradient(135deg,#e94560,#b5213a)", border: S.logoUrl ? `1.5px solid ${T.border}` : "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 900, color: "#fff", flexShrink: 0, boxShadow: S.logoUrl ? "none" : "0 4px 16px #e9456044", overflow: "hidden" }}>
                {S.logoUrl
                  ? <img src={S.logoUrl} alt={S.name} style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4 }} />
                  : (S.name?.[0] || "K")}
              </div>
              <div>
                <h2 style={{ color: T.text, fontWeight: 900, fontSize: 18, margin: 0, letterSpacing: -.5 }}>{S.name}</h2>
                <p style={{ color: T.textFaint, fontSize: 12, margin: "2px 0 0" }}>{S.tagline}</p>
              </div>
            </div>
            <button onClick={onClose} style={{ background: T.surface2, border: "none", borderRadius: 9, color: T.textMuted, padding: "7px 9px", cursor: "pointer", display: "flex", flexShrink: 0 }}><Ic.x /></button>
          </div>
          {/**<div style={{ background: "#22c55e11", border: "1px solid #22c55e33", borderRadius: 10, padding: "8px 14px", marginBottom: 20, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14 }}>🏆</span><span style={{ fontSize: 12, color: "#22c55e", fontWeight: 600 }}>{STORE_INFO.badge}</span>
          </div> */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <InfoRow icon="📞" label="Telefone" value={S.phone} href={`tel:${S.phone?.replace(/\D/g, "")}`} />
            <InfoRow icon="💬" label="WhatsApp" value={S.phone} href={`https://wa.me/${S.whatsapp}`} />
            <InfoRow icon="✉️" label="E-mail" value={S.email} href={`mailto:${S.email}`} />
            <InfoRow icon="🌐" label="Site" value={S.site} href={`https://${S.site}`} />
            <InfoRow icon="📸" label="Instagram" value={S.instagram} href={`https://instagram.com/${S.instagram?.replace("@", "")}`} />
            <InfoRow icon="📍" label="Endereço" value={S.address} />
            <InfoRow icon="🕐" label="Horário" value={S.hours} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 20 }}>
            <div style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 14px" }}><div style={{ fontSize: 18, marginBottom: 6 }}>📦</div><div style={{ fontSize: 10, color: T.textFaint, textTransform: "uppercase", letterSpacing: .8, marginBottom: 4 }}>Pedido mínimo</div><div style={{ fontSize: 13, color: T.textSub, fontWeight: 600 }}>10 unidades</div></div>
            <div style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 14px" }}><div style={{ fontSize: 18, marginBottom: 6 }}>⏱️</div><div style={{ fontSize: 10, color: T.textFaint, textTransform: "uppercase", letterSpacing: .8, marginBottom: 4 }}>Prazo de produção</div><div style={{ fontSize: 13, color: T.textSub, fontWeight: 600 }}>15–25 dias úteis</div></div>
          </div>
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 10, color: T.textFaint, textTransform: "uppercase", letterSpacing: .8, marginBottom: 10 }}>Formas de pagamento</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {S.paymentMethods.map(p => <span key={p} style={{ background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 7, padding: "5px 12px", fontSize: 12, color: T.textMuted }}>{p}</span>)}
            </div>
          </div>
          <a href={`https://wa.me/${S.whatsapp}?text=Olá! Vim pelo formulário de pedido e gostaria de mais informações.`} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 24, background: "#25d366", borderRadius: 12, padding: "13px 20px", textDecoration: "none", color: "#fff", fontWeight: 700, fontSize: 14, boxShadow: "0 4px 16px #25d36644" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.118.554 4.107 1.523 5.836L0 24l6.338-1.499A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.367l-.36-.214-3.727.881.938-3.618-.235-.372A9.818 9.818 0 012.182 12c0-5.42 4.398-9.818 9.818-9.818 5.42 0 9.818 4.398 9.818 9.818 0 5.42-4.398 9.818-9.818 9.818z" /></svg>
            Falar no WhatsApp
          </a>
        </div>
      </div>
    </>
  );
}

// ─── ROOT ────────────────────────────────────────────────────────────────────
export default function App() {
  const [isDark, setIsDark] = useState(true);
  const T = isDark ? THEMES.dark : THEMES.light;
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({});
  const [cart, setCart] = useState({});
  const [customizations, setCustomizations] = useState({});
  const [details, setDetails] = useState({ objectives: [], deliveryDate: null, role: "", description: "", attachments: [] });
  const [entries, setEntries] = useState([]); // populated when entering Step3
  const [storeOpen, setStoreOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [step3CanNext, setStep3CanNext] = useState(true);
  const [step3Missing, setStep3Missing] = useState([]);

  // 👇 modelos dinâmicos — começa vazio, populado pelo editor
  const [models, setModels] = useState([]);

  const HEADER_H = 56;
  const FOOTER_H = 72;
  const showCart = step === 3 || step === 4;
  const totalCartQty = Object.values(cart).reduce((a, b) => a + b, 0);

  const [storeInfo, setStoreInfo] = useState({
    name: "King Sports",
    tagline: "Uniformes esportivos personalizados",
    phone: "(11) 99999-0000",
    whatsapp: "5511999990000",
    email: "contato@kingsports.com.br",
    site: "www.kingsports.com.br",
    instagram: "@kingsports",
    address: "Rua dos Uniformes, 123 — São Paulo, SP",
    hours: "Seg–Sex: 8h–18h  ·  Sáb: 8h–13h",
    paymentMethods: ["Pix", "Cartão de crédito", "Boleto", "Transferência"],
    pedidoMin: "10 unidades",
    prazo: "15–25 dias úteis",
  });

  useEffect(() => {
    // editor chama isso para adicionar cada modelagem cadastrada
    window["addFormModel"] = (data) => {
      setModels(prev => {
        if (prev.find(m => m.id === data.id)) return prev;
        // designs resolvidos lazy no modal — não populamos aqui
        return [...prev, createModel({ ...data, designs: [] })];
      });
    };

    // editor chama isso para limpar e recarregar todos
    // designs NÃO são populados aqui — são resolvidos lazy no DesignsModal
    window["setFormModels"] = (list) => {
      setModels(list.map(data => createModel({ ...data, designs: [] })));
    };

    // mantido para compatibilidade, agora é no-op pois designs são lazy
    window["refreshFormDesigns"] = () => { /* lazy — resolve on modal open */ };

    window["setStoreInfo"] = (data) => {
      setStoreInfo(prev => ({
        ...prev,
        ...data,
        // aceita thumb / logo / logo_url / logoUrl como fonte da imagem
        logoUrl: data.thumb || data.logo || data.logo_url || data.logoUrl || prev.logoUrl || "",
        paymentMethods: data.pagamento
          ? data.pagamento.split(",").map(p => p.trim())
          : prev.paymentMethods,
      }));
    };
  }, []);


  return (
    <ModelsCtx.Provider value={models}>
      <StoreCtx.Provider value={storeInfo}>  {/* 👈 envolve tudo */}
        <ThemeCtx.Provider value={T}>
          <div style={{ width: "100%", position: "fixed", zIndex: "100", position: "fixed", zIndex: "100", height: "100vh", background: T.bg, fontFamily: "'Segoe UI',system-ui,sans-serif", color: T.text, display: "flex", flexDirection: "column", overflow: "hidden", transition: "background .3s, color .3s" }}>
            <style>{`
          input:focus,textarea:focus,select:focus{border-color:#e9456066!important;outline:none}
          input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}
          input[type=number]{-moz-appearance:textfield}
          ::-webkit-scrollbar{width:5px}
          ::-webkit-scrollbar-track{background:${T.surface2};border-radius:3px}
          ::-webkit-scrollbar-thumb{background:${T.border3};border-radius:3px}
          ::-webkit-scrollbar-thumb:hover{background:${T.textGhost};border-radius:3px}
          *{box-sizing:border-box}
          button:hover{opacity:.88}
          a:hover{opacity:.85}
          /* ── MOBILE ── */
          @media(max-width:480px){
            .ks-header-logo-text{display:none}
            .ks-step-label{display:none}
            .ks-toggle-label{display:none}
            .ks-card-pad{padding:12px 10px!important}
            .ks-body-pad{padding:10px 8px!important}
          }
          @media(max-width:360px){
            .ks-toggle-text{display:none}
          }
          /* ── CART DRAWER ── */
          .ks-layout{display:flex;gap:12px;align-items:stretch;}
        `}</style>

            {/* ── FIXED HEADER */}
            <div style={{ height: HEADER_H, flexShrink: 0, background: T.surface, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 12px", boxShadow: `0 2px 12px ${T.shadow}`, gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {/* Mobile: logo opens store sheet; desktop: just visual */}
                <button
                  onClick={() => { if (isMobile) setStoreOpen(true); }}
                  style={{ width: 32, height: 32, borderRadius: 9, background: storeInfo.logoUrl ? T.surface2 : "linear-gradient(135deg,#e94560,#b5213a)", border: storeInfo.logoUrl ? `1px solid ${T.border}` : "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 900, color: "#fff", flexShrink: 0, overflow: "hidden", cursor: isMobile ? "pointer" : "default", padding: 0, boxShadow: isMobile ? `0 0 0 2px ${T.border3}` : "none" }}
                >
                  {storeInfo.logoUrl
                    ? <img src={storeInfo.logoUrl} alt={storeInfo.name} style={{ width: "100%", height: "100%", objectFit: "contain", padding: 2 }} />
                    : (storeInfo.name?.[0] || "K")}
                </button>
                <div className="ks-header-logo-text">
                  <div style={{ fontSize: 13, fontWeight: 800, color: T.text, letterSpacing: -.3 }}>{storeInfo.name}</div>
                  <div style={{ fontSize: 8, color: T.textFaint, textTransform: "uppercase", letterSpacing: 1 }}>Iniciar Projeto</div>
                </div>
              </div>
              {/* Step bar — centered, flex:1 */}
              <div style={{
                flex: 1, justifyContent: "center", display: isMobile
                  ? "none"
                  : "flex"
              }}>
                <StepBar current={step} />
              </div>
              <div style={{
                textAlign: "center", marginTop: 6, fontSize: 14, color: T.text, fontWeight: "bolder", display: isMobile
                  ? "flex"
                  : "none"
              }}>Passo {step} de 4</div>
              {/* Theme toggle */}
              <div style={{ flexShrink: 0 }}>
                <ThemeToggle isDark={isDark} onToggle={() => setIsDark(d => !d)} />
              </div>
            </div>

            {/* ── SCROLLABLE BODY ── */}
            <div className="ks-body-pad" style={{ flex: 1, overflowY: "auto", padding: "16px 12px", minHeight: 0 }}>
              <div style={{ margin: "0 auto", height: "100%", display: "flex", flexDirection: "column" }}>
                <div className="ks-card-pad" style={{ background: T.surface, border: `1.5px solid ${T.border}`, borderRadius: 16, padding: "16px 14px", boxShadow: T.cardShadow, flex: 1, display: "flex", flexDirection: "column" }}>
                  {step === 1 && <Step1 profile={profile} setProfile={setProfile} onNext={() => setStep(2)} />}
                  {step === 2 && <Step2Unified cart={cart} setCart={setCart} customizations={customizations} setCustomizations={setCustomizations} entries={entries} setEntries={setEntries} onNext={() => setStep(3)} onBack={() => setStep(1)} onCanNextChange={(ok, missing) => { setStep3CanNext(ok); setStep3Missing(missing); }} />}
                  {step === 3 && <Step4 details={details} setDetails={setDetails} cart={cart} customizations={customizations} entries={entries} profile={profile} onBack={() => setStep(2)} cartBtn={<CartFloatBtn qty={totalCartQty} onClick={() => setCartDrawerOpen(true)} T={T} />} />}
                </div>
              </div>
            </div>

            {/* ── STEP 3 FIXED NAV BAR ── */}
            {step === 2 && (
              <div style={{ flexShrink: 0, background: T.surface, borderTop: `1.5px solid ${T.border}`, padding: "10px 16px", display: "flex", flexDirection: "column", gap: 8, boxShadow: `0 -4px 16px ${T.shadow}` }}>
                {!step3CanNext && step3Missing.length > 0 && (
                  <div style={{ background: "#e9456010", border: "1px solid #e9456033", borderRadius: 9, padding: "8px 12px", display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: "#e94560", fontWeight: 700 }}>⚠️ Design obrigatório:</span>
                    {step3Missing.map(({ entryId, modelId }) => {
                      const m = models.find(x => x.id === modelId);
                      return <span key={entryId} style={{ fontSize: 11, color: T.textSub, background: T.surface2, borderRadius: 5, padding: "1px 7px", border: `1px solid ${T.border2}` }}>{(customizations[entryId] || {}).customName || m?.name}</span>;
                    })}
                  </div>
                )}
                <div style={{ margin: "0 auto", width: "100%", display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <button onClick={() => setStep(1)} style={sBtnG(T)}><Ic.cL /> Voltar</button>
                  <button
                    onClick={() => step3CanNext && setStep(3)}
                    disabled={!step3CanNext}
                    style={{ ...sBtnP(), opacity: step3CanNext ? 1 : 0.4, cursor: step3CanNext ? "pointer" : "not-allowed" }}
                  >
                    Próximo: Detalhes <Ic.cR />
                  </button>
                </div>
              </div>
            )}

            {/* ── FIXED FOOTER — store button */}
            <div style={{ height: FOOTER_H, flexShrink: 0, background: `linear-gradient(to top, ${T.bg} 50%, transparent)`, padding: "10px 16px 16px", display: "flex", justifyContent: "center", position: "relative" }}>
              {/* Desktop: full pill button */}
              {!isMobile && (
                <button onClick={() => setStoreOpen(true)} style={{ background: T.surface, border: `1.5px solid ${T.border2}`, borderRadius: 50, padding: "8px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, boxShadow: `0 8px 32px ${T.shadow}`, transition: "all .2s", width: "100%", maxWidth: 500, justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: storeInfo.logoUrl ? T.surface2 : "linear-gradient(135deg,#e94560,#b5213a)", border: storeInfo.logoUrl ? `1px solid ${T.border}` : "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, color: "#fff", flexShrink: 0, overflow: "hidden" }}>
                      {storeInfo.logoUrl
                        ? <img src={storeInfo.logoUrl} alt={storeInfo.name} style={{ width: "100%", height: "100%", objectFit: "contain", padding: 2 }} />
                        : (storeInfo.name?.[0] || "K")}
                    </div>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: T.text }}>{storeInfo.name}</div>
                      <div style={{ fontSize: 10, color: T.textFaint }}>{storeInfo.tagline}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    <span style={{ background: "#25d36622", border: "1px solid #25d36633", borderRadius: 6, padding: "4px 9px", fontSize: 10, color: "#25d366", fontWeight: 600 }}>WhatsApp</span>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#e94560", boxShadow: "0 0 8px #e94560" }} />
                  </div>
                </button>
              )}
              {/* Mobile: invisible spacer — store icon is in header */}
            </div>

          </div>
        </ThemeCtx.Provider>
        {/* Portals — outside overflow:hidden AND outside ThemeCtx to avoid clipping */}
        <ThemeCtx.Provider value={T}>
          {cartDrawerOpen && showCart && (
            <CartDrawer cart={cart} entries={entries} customizations={customizations} onClose={() => setCartDrawerOpen(false)} />
          )}
          {storeOpen && <StoreBottomSheet onClose={() => setStoreOpen(false)} />}
        </ThemeCtx.Provider>
      </StoreCtx.Provider>
    </ModelsCtx.Provider>
  );
}